import { create } from "zustand";
import {
  AlertState,
  IgnoreZone,
  ResposneObject,
  SiteInfo,
  EvalinkSiteData,
  EvalinkSitePatchObject,
  Dispatcher,
} from "../SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../../utils/createApi";
import { concurrentAPIErrorHandler } from "../../../../utils/concurrentAPIErrorHandler"; // Add new import
import { AxiosError } from "axios";
import { getNumbersAfterAboutSite } from "../SiteAboutPageUtils/getNumbersAfterAboutSite";
import { useAppStore } from "../../../../../appStore"; // Import useAppStore
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { Dispatch, SetStateAction } from "react";
import { temporaryUpdateScheduleData } from "../SiteAboutPageUtils/temporaryUpdateScheduleData";
import { handleCreateInitialTimeSchedules } from "../SiteAboutPageUtils/handleCreateInitialTimeSchedules";
import { handleConvertSchedulesToBooleanArrays } from "../SiteAboutPageUtils/handleConvertSchedulesToBooleanArrays";
import { handleCreateDuskToDawn } from "../SiteAboutPageUtils/handleCreateDuskToDawn";
import { handleCreateDuskToDawnLocationPlusBuffer } from "../../SitesPage/SitesPageUtils/handleCreateDuskToDawnLocationPlusBuffer";
import { handleReadySchedulesForPatching } from "../SiteAboutPageUtils/handleReadySchedulesForPatching";
import { findNewScheduleIndex } from "../SiteAboutPageUtils/findNewScheduleIndex";
import { SchedulesTab } from "../SchedulesTab";
import {
  findOvernightOnlySharedProductOverlaps,
  handleCheckOverlappingSchedulesForSharedProducts,
} from "../SiteAboutPageUtils/handleCheckOverlappingSchedulesForSharedProducts";
import { overnightOverlapWarning } from "../SiteAboutPageUtils/overnightOverlapWarning";
import { logger } from "../../../../../utils/logger";
import { arrangeScheduleHealthMetricsAutopatrol } from "../SiteAboutPageUtils/arrangeScheduleHealthMetricsAutopatrol";
import {
  MetadataTagZones,
  MetricLabel,
} from "../../../EditIMetadataTagZones/EditMetadataTagZonesUtils/editIMetadataTagZonesInterfaces";
import { getNumbersAfterEditCamera } from "../../../EditCamera/EditCameraUtils/urlUtils";
import {
  LineCrossing,
  LineDirection,
} from "../../../EditCamera/editCameraStore/editCameraStore";
import {
  BackendLineDirection,
  backendToFrontendDirection,
  isLegacyDirectionFormat,
} from "../../../EditLineCrossings/editLineCrossingsStore/editLineCrossingsStore";
import { LINE_COLORS } from "../../../EditLineCrossings/EditLineCrossingsUtils/lineColors";
import { buildLineCrossingsListPath } from "../../../EditLineCrossings/EditLineCrossingsUtils/lineCrossingsListPath";
import { deriveActiveProductsFromGeneralInfo } from "../SiteAboutPageUtils/deriveActiveProductsFromGeneralInfo";
import { applyImmixSureviewSentinelFromGeneralInfoResponse } from "../SiteAboutPageUtils/checkForImmixOrSureviewOrSentinel";
import {
  fetchMetricSimplified,
  type MetricSimplifiedRow,
} from "../SiteAboutPageUtils/bulkUpdateCamerasApi";
import { activeProductsDisplayRecordFromSimplifiedRows } from "../SiteAboutPageUtils/metricSimplifiedUtils";
import { persistFlexSchedulePresetAssignmentsAfterPatch } from "../SiteAboutPageUtils/persistFlexSchedulePresetAssignmentsAfterPatch";
import { blockOnFlexPresetConflicts } from "../SiteAboutPageUtils/blockOnFlexPresetConflicts";

const returnHealthCheckName = (key: string) => {
  switch (key) {
    case "connectivity_check":
      return "Connectivity";
    case "motion_status_check":
      return "Motion Status";
    case "recording_check":
      return "Recording";
    case "scene_change_check":
      return "Scene Change";
    case "stream_quality_check":
      return "Stream Quality";
    case "image_quality_check":
      return "Image Quality";
  }
};

const sortHealthCheckMetricsData = (
  healthCheckMetricsData: Array<{
    id: number;
    metric_type: {
      value: string;
      display_name: string;
    };
    enabled: boolean;
    alert_emails: string;
  }>
): {
  connectivity_check: {
    id: number;
    enabled: boolean;
    alert_emails: string;
  };
  motion_status_check: {
    id: number;
    enabled: boolean;
    alert_emails: string;
  };
  recording_check: {
    id: number;
    enabled: boolean;
    alert_emails: string;
  };
  scene_change_check: {
    id: number;
    enabled: boolean;
    alert_emails: string;
  };
  stream_quality_check: {
    id: number;
    enabled: boolean;
    alert_emails: string;
  };
  image_quality_check: {
    id: number;
    enabled: boolean;
    alert_emails: string;
  };
} => {
  const returnedData: any = {};
  const dataArray = Array.isArray(healthCheckMetricsData)
    ? healthCheckMetricsData
    : [];

  dataArray.forEach((item) => {
    returnedData[item.metric_type.value] = {
      id: item.id,
      enabled: item.enabled,
      alert_emails: item.alert_emails,
    };
  });

  return returnedData;
};

const readyAllSchedules = (schedules: any) => {
  const returnSchedules = schedules;

  returnSchedules.forEach((schedule: any, index: number) => {
    if (schedule.start_time !== null) {
      if (schedule.start_time === ":00" || schedule.start_time === " - :00") {
        returnSchedules[index].start_time = null;
      }
    }
    if (schedule.end_time !== null) {
      if (schedule.end_time === ":00" || schedule.end_time === " - :00") {
        returnSchedules[index].end_time = null;
      }
    }
  });

  //remove any schedule object that has a null start_time and end_time and false value for "always_on"
  for (let i = 0; i < returnSchedules.length; i++) {
    if (
      returnSchedules[i].start_time === null &&
      returnSchedules[i].end_time === null &&
      returnSchedules[i].always_on === false
    ) {
      returnSchedules.splice(i, 1);
    }
  }
  return returnSchedules;
};

/** Camera+preset keys with a preset line fetch in progress (dedupes concurrent row renders). */
const presetLineCrossingsInFlight = new Set<string>();

/**
 * Map a `/line_crossing/` list response (paginated `{results}` or a bare
 * list) to overlay lines with display colors. Direction handling mirrors the
 * Edit Line Crossings store: legacy AtoB/BtoA/Both values are kept, backend
 * l2r/r2l/both values are converted relative to the line's coordinates.
 */
export const mapLineCrossingRowsToOverlayLines = (
  data: unknown
): LineCrossing[] => {
  const rows: any[] = Array.isArray((data as any)?.results)
    ? (data as any).results
    : Array.isArray(data)
    ? (data as any[])
    : [];
  return rows.map((line: any, index: number) => {
    let frontendDirection: LineDirection;
    if (isLegacyDirectionFormat(line.direction || "")) {
      frontendDirection = (line.direction as LineDirection) || "Both";
    } else {
      frontendDirection = backendToFrontendDirection(
        (line.direction as BackendLineDirection) || "both",
        line.coordinates
      );
    }
    return {
      id: line.id,
      camera: line.camera,
      metric_labels: line.metric_labels || [],
      coordinates: line.coordinates,
      direction: frontendDirection,
      preset: line.preset ?? null,
      color: LINE_COLORS[index % LINE_COLORS.length],
    };
  });
};

export interface CameraRowPrefetchStatus {
  statusLoaded: boolean;
  statusFailed: boolean;
  ignoreZonesLoaded: boolean;
  ignoreZonesFailed: boolean;
  tagZonesLoaded: boolean;
  tagZonesFailed: boolean;
  lineCrossingsLoaded: boolean;
  lineCrossingsFailed: boolean;
}

export interface AutoPatrolSchedule {
  id: number;
  customer: {
    id: number;
    name: string;
  };
  scheduleId: string;
  cronExpression: string;
  cronExpressionExtended: string;
  cronDescription: string;
  scheduleStatus: string;
  tier: number;
  title: string;
  win32TimeZoneId: string;
  olsonTimeZoneId: string;
  patrolType: string;
  checksPerDay: number | null;
  createdDate: string;
  updatedDate: string;
  sensitivityLevel: string;
  cadence: number | null;
  cadenceUnit: string | null;
  devices: Array<{
    id: number;
    deviceId: number;
    deviceName: string;
    isPTZ: boolean;
    deviceType: string;
  }>;
  scheduleCameraCount: number;
  siteCameraCount: number;
}

export interface AutoPatrolFlexIgnoreZonesPreset {
  localId: string;
  cameraId: number;
  name: string;
  zones: IgnoreZone[];
  persistedId: number | null;
  source: "default" | "custom";
  userNamed: boolean;
}

interface SiteAboutState {
  siteInfo: SiteInfo;
  setSiteInfo: (siteInfo: SiteInfo) => void;
  getSiteInfo: (
    retryFunction: any,
    navigate: NavigateFunction
  ) => Promise<void>;

  // General Info Tab state for AutoPatrol
  vmsVariables: (string | string[] | undefined)[][];
  setVmsVariables: (vmsVariables: (string | string[] | undefined)[][]) => void;
  generalInfoTabPatchObject: any;
  setGeneralInfoTabPatchObject: (generalInfoTabPatchObject: any) => void;
  generalInfoTabChangesMade: boolean;
  setGeneralInfoTabChangesMade: (generalInfoTabChangesMade: boolean) => void;

  backdropLoadingScreenOnEditingSchedule: boolean;
  setBackdropLoadingScreenOnEditingSchedule: (
    backdropLoadingScreenOnEditingSchedule: boolean
  ) => void;

  vCHOnlySchedules: boolean;
  setvCHOnlySchedules: (vCHOnlySchedules: boolean) => void;

  smtpInstructions: string;
  setSmtpInstructions: (smtpInstructions: string) => void;
  getSMTPIntructions: (
    retryFunction: any,
    navigate: NavigateFunction
  ) => Promise<void>;

  // New function to load all data concurrently
  loadAllData: (navigate: NavigateFunction) => Promise<void>;

  hideUseMotion: boolean;
  setHideUseMotion: (hideUseMotion: boolean) => void;

  siteAboutAlerts: {
    scheduleSubmittedAndSiteSynced: boolean;
  };

  allIgnoreZones: IgnoreZone[];
  setAllIgnoreZones: (ignoreZones: IgnoreZone[]) => void;

  visibleIgnoreZones: IgnoreZone[];
  setVisibleIgnoreZones: (visibleIgnoreZones: IgnoreZone[]) => void;
  cameraStatusById: { [cameraId: number]: ResposneObject };
  setCameraStatusById: (cameraStatusById: {
    [cameraId: number]: ResposneObject;
  }) => void;
  setCameraStatusForCamera: (cameraId: number, status: ResposneObject) => void;
  ignoreZonesByCamera: { [cameraId: number]: IgnoreZone[] };
  setIgnoreZonesByCamera: (ignoreZonesByCamera: {
    [cameraId: number]: IgnoreZone[];
  }) => void;
  setIgnoreZonesForCamera: (
    cameraId: number,
    ignoreZones: IgnoreZone[]
  ) => void;
  metadataTagZonesByCamera: { [cameraId: number]: MetadataTagZones };
  setMetadataTagZonesByCamera: (metadataTagZonesByCamera: {
    [cameraId: number]: MetadataTagZones;
  }) => void;
  setMetadataTagZonesForCamera: (
    cameraId: number,
    metadataTagZones: MetadataTagZones
  ) => void;
  rowPrefetchStateByCamera: { [cameraId: number]: CameraRowPrefetchStatus };
  setRowPrefetchStateByCamera: (rowPrefetchStateByCamera: {
    [cameraId: number]: CameraRowPrefetchStatus;
  }) => void;
  setRowPrefetchStateForCamera: (
    cameraId: number,
    state: CameraRowPrefetchStatus
  ) => void;

  setSiteAboutAlerts: (siteAboutAlerts: {
    scheduleSubmittedAndSiteSynced: boolean;
  }) => void;

  healthCheckMetricsData: {
    connectivity_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    motion_status_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    recording_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    scene_change_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    stream_quality_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    image_quality_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
  };

  setHealthCheckMetricsData: (healthCheckMetricsData: {
    connectivity_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    motion_status_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    recording_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    scene_change_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    stream_quality_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
    image_quality_check: {
      id: number;
      enabled: boolean;
      alert_emails: string;
    };
  }) => void;

  getHealthCheckMetricsData: (
    navigate: NavigateFunction,
    siteId: string
  ) => Promise<void>;

  patchHealthCheckMetricsData: (
    navigate: NavigateFunction,
    id: number,
    patchObject: {
      enabled?: boolean;
      alert_emails?: string;
    },
    key: string
  ) => Promise<void>;

  healthCheckMetricsUpdated: {
    connectivity_check: boolean;
    motion_status_check: boolean;
    recording_check: boolean;
    scene_change_check: boolean;
    stream_quality_check: boolean;
    image_quality_check: boolean;
  };
  setHealthCheckMetricsUpdated: (healthCheckMetricsUpdated: {
    connectivity_check: boolean;
    motion_status_check: boolean;
    recording_check: boolean;
    scene_change_check: boolean;
    stream_quality_check: boolean;
    image_quality_check: boolean;
  }) => void;

  // Site Schedules State
  siteSchedules: any;
  setSiteSchedules: (siteSchedules: any) => void;

  backupSiteSchedules: any;
  setBackupSiteSchedules: (backupSiteSchedules: any) => void;

  handleFilterCameras: (cameras: any) => any[];

  getNonFlexSchedules: (
    siteCameraCount: number,
    setAlertState: Dispatch<SetStateAction<AlertState>>,
    getHealthCheckData: () => void,
    navigate: NavigateFunction
  ) => Promise<void>;

  getFlexSchedules: (
    siteCameraCount: number,
    setAlertState: Dispatch<SetStateAction<AlertState>>,
    getHealthCheckData: () => void,
    navigate: NavigateFunction
  ) => Promise<void>;

  patchSchedules: (
    setEditMode: any,
    setActiveTab: any,
    siteInfo: SiteInfo,
    navigate: NavigateFunction
  ) => Promise<void>;

  patchDetailedSchedule: (
    patchObject: any,
    setEditMode: any,
    setActiveTab: any,
    siteInfo: SiteInfo,
    navigate: NavigateFunction
  ) => Promise<void>;

  handleFlexSchedulingToggle: (
    enabled: boolean,
    setSiteInfo: any,
    setEditMode: any,
    setActiveTab: any,
    siteInfo: any,
    navigate: NavigateFunction
  ) => Promise<void>;

  handleSubmitEmptySchedule: (
    setEditMode: any,
    setActiveTab: any,
    siteInfo: any,
    navigate: NavigateFunction
  ) => Promise<void>;

  handleAddSchedule: (navigate: NavigateFunction) => Promise<void>;

  handleDeleteSchedule: (
    scheduleId: string,
    navigate: NavigateFunction
  ) => Promise<void>;

  schedulesIndex: any;
  setSchedulesIndex: (schedulesIndex: any) => void;

  timeRanges: any;
  setTimeRanges: (timeRanges: any) => void;

  grid: any;
  setGrid: (grid: any) => void;

  duskToDawn: any;
  setDuskToDawn: (duskToDawn: any) => void;

  duskToDawnLocation: any;
  setDuskToDawnLocation: (duskToDawnLocation: any) => void;

  clickedSchedulesTab: any;
  setClickedSchedulesTab: (clickedSchedulesTab: any) => void;

  visibleSchedules: any;
  setVisibleSchedules: (visibleSchedules: any) => void;

  scheduleGridEditable: boolean;
  setScheduleGridEditable: (scheduleGridEditable: boolean) => void;

  scheduleChangesMade: boolean;
  setScheduleChangesMade: (scheduleChangesMade: boolean) => void;

  selectedDayInfo: any;
  setSelectedDayInfo: (selectedDayInfo: any) => void;

  matchingSchedules: number[][];
  setMatchingSchedules: (matchingSchedules: number[][]) => void;

  flexSchedulesOn: boolean;
  setFlexSchedulesOn: (flexSchedulesOn: boolean) => void;

  editTimeScheduleDialogOpen: boolean;
  setEditTimeScheduleDialogOpen: (editTimeScheduleDialogOpen: boolean) => void;

  editActiveProductsDialogOpen: boolean;
  setEditActiveProductsDialogOpen: (
    editActiveProductsDialogOpen: boolean
  ) => void;

  editScheduleNameDialogOpen: boolean;
  setEditScheduleNameDialogOpen: (editScheduleNameDialogOpen: boolean) => void;

  deleteScheduleDialogOpen: boolean;
  setDeleteScheduleDialogOpen: (deleteScheduleDialogOpen: boolean) => void;

  deactivateSiteInsteadDialogOpen: boolean;
  setDeactivateSiteInsteadDialogOpen: (
    deactivateSiteInsteadDialogOpen: boolean
  ) => void;

  schedulesBackdropLoadingScreenOn: boolean;
  setSchedulesBackdropLoadingScreenOn: (
    schedulesBackdropLoadingScreenOn: boolean
  ) => void;

  healthCheckDataZustand: any;
  setHealthCheckDataZustand: (healthCheckDataZustand: any) => void;

  metadataTagZones: MetadataTagZones;
  setMetadataTagZones: (metadataTagZones: MetadataTagZones) => void;

  autoPatrolSchedules: AutoPatrolSchedule[];
  selectedAutoPatrolScheduleIndex: number;
  autoPatrolFlexIgnoreZonesEnabledBySite: Record<number, boolean>;
  setAutoPatrolFlexIgnoreZonesEnabledForSite: (
    siteId: number,
    enabled: boolean
  ) => void;
  autoPatrolFlexIgnoreZonesWizardOpenBySite: Record<number, boolean>;
  setAutoPatrolFlexIgnoreZonesWizardOpenForSite: (
    siteId: number,
    open: boolean
  ) => void;
  autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite: Record<number, number>;
  setAutoPatrolFlexIgnoreZonesActiveScheduleIndexForSite: (
    siteId: number,
    index: number
  ) => void;
  autoPatrolFlexIgnoreZonesPresetsByCamera: Record<
    number,
    AutoPatrolFlexIgnoreZonesPreset[]
  >;
  setAutoPatrolFlexIgnoreZonesPresetsForCamera: (
    cameraId: number,
    presets: AutoPatrolFlexIgnoreZonesPreset[]
  ) => void;
  autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera: Record<
    string,
    string
  >;
  setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera: (
    scheduleId: number,
    cameraId: number,
    presetLocalId: string
  ) => void;
  autoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule: Record<
    number,
    number[]
  >;
  setAutoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule: (
    scheduleId: number,
    cameraIds: number[]
  ) => void;
  initializeAutoPatrolFlexIgnoreZonesForSite: (params: {
    siteId: number;
    scheduleIds: number[];
    cameraIdsBySchedule: Record<number, number[]>;
  }) => void;
  resetAutoPatrolFlexIgnoreZonesForSite: (siteId: number) => void;
  autoPatrolSchedulesUpdatedBoolean: boolean;
  setAutoPatrolSchedulesUpdatedBoolean: (
    autoPatrolSchedulesUpdatedBoolean: boolean
  ) => void;
  updatedAutoPatrolScheduleIndexes: string[];
  setUpdatedAutoPatrolScheduleIndexes: (
    updatedAutoPatrolScheduleIndexes: string[]
  ) => void;
  autoPatrolScheduleHealthchckMetrics: any;
  setAutoPatrolScheduleHealthchckMetrics: (
    autoPatrolScheduleHealthchckMetrics: any
  ) => void;
  autoPatrolScheduleHealthchckMetricsUpdatedBoolean: boolean;
  setAutoPatrolScheduleHealthchckMetricsUpdatedBoolean: (
    autoPatrolScheduleHealthchckMetricsUpdatedBoolean: boolean
  ) => void;
  updatedAutoPatrolScheduleHealthchckMetricsIds: number[];
  setUpdatedAutoPatrolScheduleHealthchckMetricsIds: (
    updatedAutoPatrolScheduleHealthchckMetricsIds: number[]
  ) => void;
  getAutoPatrolScheduleHealthchckMetrics: (
    navigate: NavigateFunction,
    scheduleId: string,
    finalIndex: boolean
  ) => Promise<void>;
  patchAutoPatrolScheduleHealthchckMetrics: (
    navigate: NavigateFunction,
    id: number,
    activeBoolean: boolean,
    finalIndex: boolean,
    minimumResolutionHeight: number
  ) => Promise<void>;
  setSelectedAutoPatrolScheduleIndex: (
    selectedAutoPatrolScheduleIndex: number
  ) => void;
  autopatrolCamerasTablePage: number;
  setAutopatrolCamerasTablePage: (page: number) => void;
  autopatrolCamerasTableRowsPerPage: number;
  setAutopatrolCamerasTableRowsPerPage: (rowsPerPage: number) => void;
  setAutoPatrolSchedules: (autoPatrolSchedules: AutoPatrolSchedule[]) => void;
  getAutoPatrolSchedules: (
    navigate: NavigateFunction,
    siteId: string
  ) => Promise<void>;

  patchAutoPatrolSchedules: (
    navigate: NavigateFunction,
    index: number
  ) => Promise<void>;

  syncSiteNeeded: boolean;
  setSyncSiteNeeded: (syncSiteNeeded: boolean) => void;

  // True from the moment a manual sync's deploy_next is accepted until the
  // backend confirms settings_deployed (deploys run async in django-q).
  syncPending: boolean;
  setSyncPending: (syncPending: boolean) => void;

  syncSiteNeededAlertOpen: boolean;
  setSyncSiteNeededAlertOpen: (syncSiteNeededAlertOpen: boolean) => void;

  checkIfSyncNeeded: (navigate: NavigateFunction) => Promise<void>;

  activeCameraProducts: {
    [cameraId: number]: { [productName: string]: boolean };
  };
  setActiveCameraProducts: (activeCameraProducts: {
    [cameraId: number]: { [productName: string]: boolean };
  }) => void;
  activeCameraProductsLoading: { [cameraId: number]: true };
  setActiveCameraProductsLoading: (loading: {
    [cameraId: number]: true;
  }) => void;
  setActiveCameraProductsForCamera: (
    cameraId: number,
    products: Record<string, boolean>
  ) => void;
  getActiveCameraProductsForCamera: (
    navigate: NavigateFunction,
    cameraId: number
  ) => Promise<void>;
  getActiveCameraProductsForAllCameras: (
    navigate: NavigateFunction,
    cameraIds: number[]
  ) => Promise<void>;
  chmOnlyIgnoreZonesByCamera: { [cameraId: number]: boolean };
  setChmOnlyIgnoreZonesByCamera: (map: { [cameraId: number]: boolean }) => void;
  setChmOnlyIgnoreZonesForCamera: (
    cameraId: number,
    isChmOnly: boolean
  ) => void;

  cameraSlice: { [cameraId: number]: boolean };
  setCameraSlice: (cameraSlice: { [cameraId: number]: boolean }) => void;
  setCameraSliceForCamera: (cameraId: number, slice: boolean) => void;

  /** Stream ids per camera from last general_info fetch (used for bulk slice updates). */
  cameraIdToStreamIds: { [cameraId: number]: number[] };
  setCameraStreamIdsForCamera: (cameraId: number, streamIds: number[]) => void;
  clearBulkCameraCachesForCameras: (cameraIds: number[]) => void;

  /** Last GET /metric/simplified/?customer= response (e.g. Flex Ignore Zones label_id). */
  siteMetricsSimplified: MetricSimplifiedRow[];
  siteMetricsSimplifiedCustomerId: number | null;
  setSiteMetricsSimplifiedData: (
    rows: MetricSimplifiedRow[],
    customerId: number | null
  ) => void;
  prefetchSiteMetricsSimplified: (
    navigate: NavigateFunction,
    customerId: number | string | null | undefined
  ) => Promise<void>;
  refreshActiveCameraProductsFromSimplifiedEndpoint: (
    navigate: NavigateFunction,
    customerId: number | string,
    cameraIds: number[]
  ) => Promise<void>;
  prefetchBulkDialogActiveCameraProducts: (
    navigate: NavigateFunction,
    customerId: number | undefined | null,
    cameraIds: number[]
  ) => Promise<void>;

  resetSiteAboutState: () => void;

  handleUpdateTimeSchedulesForAddSchedule: (
    dayOfWeekArray: number[],
    startTime: string,
    endTime: string,
    alwaysOn: boolean,
    duskToDawn: boolean,
    bufferTime: number,
    duskToDawnLocation: string
  ) => void;

  // Site About → Webhooks → "+ Add Webhook" (ENG-2233; opens WebhookSetupDialog
  // since ENG-2427). Lives here so the tab can open the dialog without
  // threading another prop through SiteAbout.
  addSiteWebhookDialogOpen: boolean;
  setAddSiteWebhookDialogOpen: (addSiteWebhookDialogOpen: boolean) => void;
  // ENG-2427: the site webhook being edited / deleted from the Webhooks tab.
  editSiteWebhookId: number | null;
  setEditSiteWebhookId: (editSiteWebhookId: number | null) => void;
  deleteSiteWebhookId: number | null;
  setDeleteSiteWebhookId: (deleteSiteWebhookId: number | null) => void;

  backupWebhookData: Array<{
    id: number;
    customer: number;
    display_name: string;
    webhook_type: string;
    target_url: string;
    method: string;
    auth_type: string | null;
    auth_endpoint: string | null;
    auth_body: string | null;
    auth_token_path: string | null;
    message_body: string;
  }>;
  setBackupWebhookData: (
    backupWebhookData: Array<{
      id: number;
      customer: number;
      display_name: string;
      webhook_type: string;
      target_url: string;
      method: string;
      auth_type: string | null;
      auth_endpoint: string | null;
      auth_body: string | null;
      auth_token_path: string | null;
      message_body: string;
    }>
  ) => void;

  getMetadataTagZones: (navigate: NavigateFunction) => Promise<void>;

  autoPatrolCameraProducts: {
    [key: number]: Array<{
      id: number;
      schedule: number;
      metric_label: {
        id: number;
        name: string;
        display_name: string;
        order: number | null;
        product: {
          id: number;
          name: string;
          value: string;
          order: number | null;
        };
      };
      enabled: boolean;
    }>;
  };
  setAutoPatrolCameraProducts: (autoPatrolCameraProducts: {
    [key: number]: Array<{
      id: number;
      schedule: number;
      metric_label: {
        id: number;
        name: string;
        display_name: string;
        order: number | null;
        product: {
          id: number;
          name: string;
          value: string;
          order: number | null;
        };
      };
      enabled: boolean;
    }>;
  }) => void;
  getAutoPatrolCameraProducts: (
    navigate: NavigateFunction,
    scheduleId: number
  ) => Promise<void>;

  updatedProductMetricsIds: {
    [key: number]: number[];
  };
  setUpdatedProductMetricsIds: (updatedProductMetricsIds: {
    [key: number]: number[];
  }) => void;

  productMetricsWereUpdated: boolean;
  setProductMetricsWereUpdated: (productMetricsWereUpdated: boolean) => void;

  updateScheduleProducts: (
    productId: number,
    enabled: boolean,
    scheduleId: number,
    navigate: NavigateFunction
  ) => Promise<void>;

  // Line crossings data
  lineCrossingsData: { [key: number]: LineCrossing[] };
  setLineCrossingsData: (lineCrossingsData: {
    [key: number]: LineCrossing[];
  }) => void;

  lineCrossingsMetricLabels: MetricLabel[];
  setLineCrossingsMetricLabels: (
    lineCrossingsMetricLabels: MetricLabel[]
  ) => void;

  getLineCrossingsData: (
    navigate: NavigateFunction,
    cameraId: number
  ) => Promise<void>;

  /**
   * Lines scoped to a named preset, keyed camera id → preset id. Loaded on
   * demand when the camera row's flex preset picker selects a named preset
   * (ENG-1610); `lineCrossingsData` keeps holding the camera-wide Default lines.
   */
  presetLineCrossingsData: {
    [cameraId: number]: { [presetId: number]: LineCrossing[] };
  };
  getPresetLineCrossingsData: (
    navigate: NavigateFunction,
    cameraId: number,
    presetId: number
  ) => Promise<void>;

  checkForEvalinkSiteId: (
    siteId: string,
    navigate: NavigateFunction
  ) => Promise<void>;
  evalinkSite: boolean;
  setEvalinkSite: (evalinkSite: boolean) => void;
  evalinkData: EvalinkSiteData;
  setEvalinkData: (evalinkData: EvalinkSiteData) => void;
  getEvalinkData: (navigate: NavigateFunction) => Promise<void>;
  evalinkChangesMade: boolean;
  setEvalinkChangesMade: (evalinkChangesMade: boolean) => void;
  evalinkPatchObject: EvalinkSitePatchObject;
  setEvalinkPatchObject: (evalinkPatchObject: EvalinkSitePatchObject) => void;
  evalinkSiteId: number;
  setEvalinkSiteId: (evalinkSiteId: number) => void;
  handlePatchEvalink: (
    navigate: NavigateFunction,
    setBackdropLoadingScreenOn: Dispatcher<boolean>,
    setEditMode: Dispatcher<boolean>,
    onSuccess?: () => void
  ) => Promise<void>;
}

export const useSiteAboutStore = create<SiteAboutState>((set, get) => ({
  siteInfo: {} as SiteInfo,
  setSiteInfo: (siteInfo: SiteInfo) => set({ siteInfo }),

  getSiteInfo: async (retryFunction, navigate) => {
    const { setSiteInfo } = get();
    // Get the setter from useAppStore
    const AxiosUI = createApi(``);
    // const retryFunction = () => {
    //     get().getSiteInfo(navigate);
    // };

    try {
      const response = await AxiosUI.get(
        `customer/${getNumbersAfterAboutSite(location.href)}/about/`
      );
      const siteInfo = response.data;
      setSiteInfo(siteInfo);
    } catch (reason) {
      concurrentAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setSiteInfo({} as SiteInfo);
        }
      );
    }
  },

  // General Info Tab state for AutoPatrol
  vmsVariables: [],
  setVmsVariables: (vmsVariables: (string | string[] | undefined)[][]) =>
    set({ vmsVariables }),
  generalInfoTabPatchObject: {},
  setGeneralInfoTabPatchObject: (generalInfoTabPatchObject: any) =>
    set({ generalInfoTabPatchObject }),
  generalInfoTabChangesMade: false,
  setGeneralInfoTabChangesMade: (generalInfoTabChangesMade: boolean) =>
    set({ generalInfoTabChangesMade }),

  backdropLoadingScreenOnEditingSchedule: false,
  setBackdropLoadingScreenOnEditingSchedule: (
    backdropLoadingScreenOnEditingSchedule: boolean
  ) => set({ backdropLoadingScreenOnEditingSchedule }),

  vCHOnlySchedules: false,
  setvCHOnlySchedules: (vCHOnlySchedules: boolean) => set({ vCHOnlySchedules }),

  smtpInstructions: "",
  setSmtpInstructions: (smtpInstructions: string) => set({ smtpInstructions }),
  getSMTPIntructions: async (retryFunction, navigate) => {
    const { setSmtpInstructions } = get();
    // Get the setter from useAppStore
    const AxiosUI = createApi(``);
    try {
      const response = await AxiosUI.get(
        `customer/${getNumbersAfterAboutSite(location.href)}/instructions/`
      );
      const smtpInstructions = response.data;
      setSmtpInstructions(smtpInstructions);
    } catch (reason) {
      concurrentAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setSmtpInstructions("");
        }
      );
    }
  },

  // New function that loads all data concurrently
  loadAllData: async (navigate) => {
    const { getSiteInfo, getSMTPIntructions } = get();
    const retryLoadAllData = () => get().loadAllData(navigate);

    try {
      // Run both API calls concurrently and wait for both to settle
      await Promise.allSettled([
        getSiteInfo(retryLoadAllData, navigate),
        getSMTPIntructions(retryLoadAllData, navigate),
      ]);
    } catch (error) {
      // Catch any unexpected errors during the allSettled process itself (unlikely)
      logger.error("Unexpected error during loadAllData:", error);
      // Define the retry function
      concurrentAPIErrorHandler(
        error as AxiosError,
        navigate,
        retryLoadAllData,
        () => {}
      );
    } finally {
      // This block will always execute after the try (and catch, if an error occurred)
      // ensuring the loading screen is turned off regardless of success or failure.
      const refreshingToken = useAppStore.getState().refreshingToken;
      const setBackdropLoadingScreenRefreshTokenOn =
        useAppStore.getState().setBackdropLoadingScreenRefreshTokenOn;
      if (!refreshingToken) {
        setBackdropLoadingScreenRefreshTokenOn(false);
      }
    }
  },
  hideUseMotion: false,
  setHideUseMotion: (hideUseMotion: boolean) => set({ hideUseMotion }),

  siteAboutAlerts: {
    scheduleSubmittedAndSiteSynced: false,
  },

  allIgnoreZones: [],
  setAllIgnoreZones: (allIgnoreZones: IgnoreZone[]) => set({ allIgnoreZones }),
  visibleIgnoreZones: [],
  setVisibleIgnoreZones: (visibleIgnoreZones: IgnoreZone[]) =>
    set({ visibleIgnoreZones }),
  cameraStatusById: {} as { [cameraId: number]: ResposneObject },
  setCameraStatusById: (cameraStatusById: {
    [cameraId: number]: ResposneObject;
  }) => set({ cameraStatusById }),
  setCameraStatusForCamera: (cameraId: number, status: ResposneObject) => {
    const { cameraStatusById, setCameraStatusById } = get();
    setCameraStatusById({
      ...cameraStatusById,
      [cameraId]: status,
    });
  },
  ignoreZonesByCamera: {} as { [cameraId: number]: IgnoreZone[] },
  setIgnoreZonesByCamera: (ignoreZonesByCamera: {
    [cameraId: number]: IgnoreZone[];
  }) => set({ ignoreZonesByCamera }),
  setIgnoreZonesForCamera: (cameraId: number, ignoreZones: IgnoreZone[]) => {
    const { ignoreZonesByCamera, setIgnoreZonesByCamera } = get();
    setIgnoreZonesByCamera({
      ...ignoreZonesByCamera,
      [cameraId]: ignoreZones,
    });
  },
  metadataTagZonesByCamera: {} as { [cameraId: number]: MetadataTagZones },
  setMetadataTagZonesByCamera: (metadataTagZonesByCamera: {
    [cameraId: number]: MetadataTagZones;
  }) => set({ metadataTagZonesByCamera }),
  setMetadataTagZonesForCamera: (
    cameraId: number,
    metadataTagZones: MetadataTagZones
  ) => {
    const { metadataTagZonesByCamera, setMetadataTagZonesByCamera } = get();
    setMetadataTagZonesByCamera({
      ...metadataTagZonesByCamera,
      [cameraId]: metadataTagZones,
    });
  },
  rowPrefetchStateByCamera: {} as {
    [cameraId: number]: CameraRowPrefetchStatus;
  },
  setRowPrefetchStateByCamera: (rowPrefetchStateByCamera: {
    [cameraId: number]: CameraRowPrefetchStatus;
  }) => set({ rowPrefetchStateByCamera }),
  setRowPrefetchStateForCamera: (
    cameraId: number,
    state: CameraRowPrefetchStatus
  ) => {
    const { rowPrefetchStateByCamera, setRowPrefetchStateByCamera } = get();
    setRowPrefetchStateByCamera({
      ...rowPrefetchStateByCamera,
      [cameraId]: state,
    });
  },

  setSiteAboutAlerts: (siteAboutAlerts) => set({ siteAboutAlerts }),

  healthCheckMetricsData: {
    connectivity_check: {
      id: 0,
      enabled: false,
      alert_emails: "",
    },
    motion_status_check: {
      id: 0,
      enabled: false,
      alert_emails: "",
    },
    recording_check: {
      id: 0,
      enabled: false,
      alert_emails: "",
    },
    scene_change_check: {
      id: 0,
      enabled: false,
      alert_emails: "",
    },
    stream_quality_check: {
      id: 0,
      enabled: false,
      alert_emails: "",
    },
    image_quality_check: {
      id: 0,
      enabled: false,
      alert_emails: "",
    },
  },

  // Method to set healthCheckMetricsData
  setHealthCheckMetricsData: (healthCheckMetricsData) =>
    set({ healthCheckMetricsData }),

  getHealthCheckMetricsData: async (navigate, siteId) => {
    const { setHealthCheckMetricsData } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getHealthCheckMetricsData(navigate, siteId);
    };

    const defaultHealthCheckMetrics = {
      connectivity_check: {
        id: 0,
        enabled: false,
        alert_emails: "",
      },
      motion_status_check: {
        id: 0,
        enabled: false,
        alert_emails: "",
      },
      recording_check: {
        id: 0,
        enabled: false,
        alert_emails: "",
      },
      scene_change_check: {
        id: 0,
        enabled: false,
        alert_emails: "",
      },
      stream_quality_check: {
        id: 0,
        enabled: false,
        alert_emails: "",
      },
      image_quality_check: {
        id: 0,
        enabled: false,
        alert_emails: "",
      },
    };

    try {
      const response = await AxiosUI.get(
        `metric_healthcheck/by_customer/${siteId}`
      );
      const rawData = response.data;
      const dataArray = Array.isArray(rawData) ? rawData : [];
      const sortedData =
        dataArray.length > 0
          ? sortHealthCheckMetricsData(dataArray)
          : defaultHealthCheckMetrics;
      setHealthCheckMetricsData(sortedData);
      localStorage.setItem(
        "backupHealthCheckMetricsData",
        JSON.stringify(sortedData)
      );
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setHealthCheckMetricsData(defaultHealthCheckMetrics);
        }
      );
    }
  },
  patchHealthCheckMetricsData: async (navigate, id, patchObject, key) => {
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().patchHealthCheckMetricsData(navigate, id, patchObject, key);
    };

    try {
      await AxiosUI.patch(`metric_healthcheck/${id}/`, patchObject);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert(
            `Trouble updating ${returnHealthCheckName(
              key
            )} data. Please try again.`
          );
        }
      );
    }
  },

  healthCheckMetricsUpdated: {
    connectivity_check: false,
    motion_status_check: false,
    recording_check: false,
    scene_change_check: false,
    stream_quality_check: false,
    image_quality_check: false,
  },
  setHealthCheckMetricsUpdated: (healthCheckMetricsUpdated) =>
    set({ healthCheckMetricsUpdated }),

  siteSchedules: [],
  setSiteSchedules: (siteSchedules: any) => set({ siteSchedules }),

  backupSiteSchedules: [],
  setBackupSiteSchedules: (backupSiteSchedules: any) =>
    set({ backupSiteSchedules }),

  handleFilterCameras: (cameras: any) => {
    const { autoPatrolSchedules, selectedAutoPatrolScheduleIndex } = get();

    const activeCameraIds = autoPatrolSchedules[
      selectedAutoPatrolScheduleIndex
    ]?.devices.map((schedule: any) => schedule.id);

    return cameras.filter((camera: any) =>
      activeCameraIds?.includes(camera.id)
    );
  },

  getNonFlexSchedules: async (
    siteCameraCount,
    setAlertState,
    getHealthCheckData,
    navigate
  ) => {
    const {
      setSiteSchedules,
      setVisibleSchedules,
      setTimeRanges,
      setGrid,
      setDuskToDawn,
      setDuskToDawnLocation,
      setBackupSiteSchedules,
      setFlexSchedulesOn,
    } = get();
    const AxiosUI = createApi(``);

    setFlexSchedulesOn(false);

    const retryFunction = () => {
      get().getNonFlexSchedules(
        siteCameraCount,
        setAlertState,
        getHealthCheckData,
        navigate
      );
    };
    try {
      const response = await AxiosUI.get(
        `schedule/by_customer/${getNumbersAfterAboutSite(location.href)}/`
      );
      const temporaryUpdatedScheduleData = temporaryUpdateScheduleData(
        response.data
      );

      setTimeRanges(
        handleCreateInitialTimeSchedules(
          temporaryUpdatedScheduleData[0].schedule
        )
      );
      setGrid(
        handleConvertSchedulesToBooleanArrays(
          temporaryUpdatedScheduleData[0].schedule
        )
      );
      setDuskToDawn(
        handleCreateDuskToDawn(temporaryUpdatedScheduleData[0].schedule)
      );
      setDuskToDawnLocation(
        handleCreateDuskToDawnLocationPlusBuffer(
          temporaryUpdatedScheduleData[0].schedule
        )
      );
      setVisibleSchedules({
        one: true,
        two: false,
        three: false,
      });
      setSiteSchedules(temporaryUpdatedScheduleData);
      setBackupSiteSchedules(temporaryUpdatedScheduleData);
      localStorage.setItem(
        "backUpStandardSchedule",
        JSON.stringify(temporaryUpdatedScheduleData)
      );
      if (siteCameraCount === 0 && response.data.length === 0) {
        setAlertState((previousState: AlertState) => {
          return { ...previousState, noCameraOrSchedule: true };
        });
      }
      if (siteCameraCount > 0 && response.data.length === 0) {
        setAlertState((previousState: AlertState) => {
          return { ...previousState, noSchedule: true };
        });
      }
      getHealthCheckData();
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setSiteSchedules([]);
          setBackupSiteSchedules([]);
          getHealthCheckData();
        }
      );
    }
  },

  getFlexSchedules: async (
    siteCameraCount,
    setAlertState,
    getHealthCheckData,
    navigate
  ) => {
    const {
      setSiteSchedules,
      setVisibleSchedules,
      setTimeRanges,
      setGrid,
      setDuskToDawn,
      setDuskToDawnLocation,
      schedulesIndex,
      setFlexSchedulesOn,
      setSelectedDayInfo,
      setBackupSiteSchedules,
    } = get();
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getFlexSchedules(
        siteCameraCount,
        setAlertState,
        getHealthCheckData,
        navigate
      );
    };

    setFlexSchedulesOn(true);

    try {
      const response = await AxiosUI.get(
        `flex_schedule/by_customer/${getNumbersAfterAboutSite(location.href)}/`
      );
      setTimeRanges(
        handleCreateInitialTimeSchedules(response.data[schedulesIndex].schedule)
      );
      setGrid(
        handleConvertSchedulesToBooleanArrays(
          response.data[schedulesIndex].schedule
        )
      );
      setDuskToDawn(
        handleCreateDuskToDawn(response.data[schedulesIndex].schedule)
      );
      setDuskToDawnLocation(
        handleCreateDuskToDawnLocationPlusBuffer(
          response.data[schedulesIndex].schedule
        )
      );
      setSelectedDayInfo(response.data[schedulesIndex]);
      setSiteSchedules(response.data);
      setBackupSiteSchedules(response.data);
      localStorage.setItem("backUpFlexSchedule", JSON.stringify(response.data));
      if (response.data.length === 1) {
        setVisibleSchedules({
          one: true,
          two: false,
          three: false,
        });
      }
      if (response.data.length === 2) {
        setVisibleSchedules({
          one: true,
          two: true,
          three: false,
        });
      }
      if (response.data.length === 3) {
        setVisibleSchedules({
          one: true,
          two: true,
          three: true,
        });
      }
      getHealthCheckData();
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setSiteSchedules([]);
          setBackupSiteSchedules([]);
          getHealthCheckData();
        }
      );
    }
  },

  patchSchedules: async (setEditMode, setActiveTab, siteInfo, navigate) => {
    const AxiosUI = createApi(``);

    const {
      flexSchedulesOn,
      siteSchedules,
      setSiteSchedules,
      setVisibleSchedules,
      setTimeRanges,
      setGrid,
      setDuskToDawn,
      setDuskToDawnLocation,
      setBackupSiteSchedules,
      setScheduleGridEditable,
      setBackdropLoadingScreenOnEditingSchedule,
      schedulesIndex,
      setSelectedDayInfo,
      setDeactivateSiteInsteadDialogOpen,
      checkIfSyncNeeded,
    } = get();

    // setBackdropLoadingScreenOnEditingSchedule(false)

    const retryFunction = () => {
      get().patchSchedules(setEditMode, setActiveTab, siteInfo, navigate);
    };

    if (!flexSchedulesOn) {
      const patchObject = handleReadySchedulesForPatching(
        siteSchedules[0].schedule
      );

      if (patchObject.length === 0) {
        setDeactivateSiteInsteadDialogOpen(true);
        return;
      }

      setBackdropLoadingScreenOnEditingSchedule(true);
      try {
        const response = await AxiosUI.patch(
          `schedule/${getNumbersAfterAboutSite(location.href)}/`,
          patchObject
        );
        checkIfSyncNeeded(navigate);
        const temporaryUpdatedScheduleData = temporaryUpdateScheduleData(
          response.data
        );
        setTimeRanges(
          handleCreateInitialTimeSchedules(
            temporaryUpdatedScheduleData[0].schedule
          )
        );
        setGrid(
          handleConvertSchedulesToBooleanArrays(
            temporaryUpdatedScheduleData[0].schedule
          )
        );
        setDuskToDawn(
          handleCreateDuskToDawn(temporaryUpdatedScheduleData[0].schedule)
        );
        setDuskToDawnLocation(
          handleCreateDuskToDawnLocationPlusBuffer(
            temporaryUpdatedScheduleData[0].schedule
          )
        );
        setSiteSchedules(temporaryUpdatedScheduleData);
        localStorage.setItem(
          "backUpStandardSchedule",
          JSON.stringify(temporaryUpdatedScheduleData)
        );
        setBackupSiteSchedules(temporaryUpdatedScheduleData);
        setEditMode(false);
        setScheduleGridEditable(false);
        setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
        setBackdropLoadingScreenOnEditingSchedule(false);
      } catch (error) {
        universalAPIErrorHandler(
          error as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Trouble updating schedule. Please try again.");
            const backupSchedules = JSON.parse(
              localStorage.getItem("backUpStandardSchedule") || "[]"
            );
            setSiteSchedules(backupSchedules);
            setGrid(
              handleConvertSchedulesToBooleanArrays(backupSchedules[0].schedule)
            );
            setDuskToDawn(handleCreateDuskToDawn(backupSchedules[0].schedule));
            setDuskToDawnLocation(
              handleCreateDuskToDawnLocationPlusBuffer(
                backupSchedules[0].schedule
              )
            );
            setTimeRanges(
              handleCreateInitialTimeSchedules(backupSchedules[0].schedule)
            );
            setVisibleSchedules({
              one: true,
              two: false,
              three: false,
            });
            localStorage.setItem(
              "siteSchedule",
              JSON.stringify(backupSchedules)
            );
            setEditMode(false);
            setScheduleGridEditable(false);
            setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
            setBackdropLoadingScreenOnEditingSchedule(false);
          }
        );
      }
    } else {
      const submittedSchedules = siteSchedules;

      siteSchedules.forEach((schedule: any, index: number) => {
        submittedSchedules[index].schedule = readyAllSchedules(
          schedule.schedule
        );
      });

      let allSchedulesHaveProducts = true;
      submittedSchedules.forEach((schedule: any) => {
        if (schedule?.product.length === 0) {
          allSchedulesHaveProducts = false;
        }
      });

      if (!allSchedulesHaveProducts) {
        alert("All schedules must have products in order to submit changes.");
        setBackdropLoadingScreenOnEditingSchedule(false);
        return;
      }

      const overlappingProducts =
        handleCheckOverlappingSchedulesForSharedProducts(submittedSchedules);

      if (overlappingProducts) {
        alert(
          "Schedules cannot be submitted with overlapping products.  Please update the schedules to remove the overlapping products and resubmit."
        );
        setBackdropLoadingScreenOnEditingSchedule(false);
        return;
      }

      // Overlaps only through the after-midnight half of an overnight window
      // are allowed (existing sites are saved that way) but duplicate alerts,
      // so say so and let the user decide (ENG-1970).
      const overnightOnly =
        findOvernightOnlySharedProductOverlaps(submittedSchedules);
      if (
        overnightOnly.length > 0 &&
        !window.confirm(overnightOverlapWarning(overnightOnly))
      ) {
        setBackdropLoadingScreenOnEditingSchedule(false);
        return;
      }

      if (await blockOnFlexPresetConflicts(siteInfo, submittedSchedules)) {
        setBackdropLoadingScreenOnEditingSchedule(false);
        return;
      }

      try {
        const response = await AxiosUI.patch(
          `/flex_schedule/by_customer/${getNumbersAfterAboutSite(
            location.href
          )}/`,
          submittedSchedules
        );
        await persistFlexSchedulePresetAssignmentsAfterPatch(
          siteInfo.id,
          response.data
        );
        checkIfSyncNeeded(navigate);
        setTimeRanges(
          handleCreateInitialTimeSchedules(
            response.data[schedulesIndex].schedule
          )
        );
        setGrid(
          handleConvertSchedulesToBooleanArrays(
            response.data[schedulesIndex].schedule
          )
        );
        setDuskToDawn(
          handleCreateDuskToDawn(response.data[schedulesIndex].schedule)
        );
        setDuskToDawnLocation(
          handleCreateDuskToDawnLocationPlusBuffer(
            response.data[schedulesIndex].schedule
          )
        );
        setSelectedDayInfo(response.data[schedulesIndex]);

        setSiteSchedules(response.data);
        setBackupSiteSchedules(response.data);
        localStorage.setItem(
          "backUpFlexSchedule",
          JSON.stringify(response.data)
        );
        setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
        setEditMode(false);
        setScheduleGridEditable(false);
        setBackdropLoadingScreenOnEditingSchedule(false);
      } catch (error) {
        universalAPIErrorHandler(
          error as AxiosError,
          navigate,
          retryFunction,
          () => {
            const backupFlexSchedules = JSON.parse(
              localStorage.getItem("backUpFlexSchedule") || "[]"
            );
            setSiteSchedules(backupFlexSchedules);
            setGrid(
              handleConvertSchedulesToBooleanArrays(
                backupFlexSchedules[schedulesIndex].schedule
              )
            );
            setDuskToDawn(
              handleCreateDuskToDawn(
                backupFlexSchedules[schedulesIndex].schedule
              )
            );
            setDuskToDawnLocation(
              handleCreateDuskToDawnLocationPlusBuffer(
                backupFlexSchedules[schedulesIndex].schedule
              )
            );
            setTimeRanges(
              handleCreateInitialTimeSchedules(
                backupFlexSchedules[schedulesIndex].schedule
              )
            );
            setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
            setEditMode(false);
            setScheduleGridEditable(false);
            setBackdropLoadingScreenOnEditingSchedule(false);
          }
        );
      }
    }
  },

  patchDetailedSchedule: async (
    patchObject,
    setEditMode,
    setActiveTab,
    siteInfo,
    navigate
  ) => {
    const AxiosUI = createApi(``);
    const {
      setSiteSchedules,
      setTimeRanges,
      setGrid,
      setDuskToDawn,
      setDuskToDawnLocation,
      setBackupSiteSchedules,
      setScheduleGridEditable,
      setBackdropLoadingScreenOnEditingSchedule,
      siteSchedules,
      flexSchedulesOn,
      schedulesIndex,
      setSelectedDayInfo,
      checkIfSyncNeeded,
    } = get();
    const retryFunction = () => {
      get().patchDetailedSchedule(
        patchObject,
        setEditMode,
        setActiveTab,
        siteInfo,
        navigate
      );
    };

    patchObject.forEach((schedule: any, index: number) => {
      if (schedule.start_time === "") {
        patchObject[index].start_time = null;
      }
      if (schedule.end_time === "") {
        patchObject[index].end_time = null;
      }
    });

    if (!flexSchedulesOn) {
      try {
        const response = await AxiosUI.patch(
          `schedule/${getNumbersAfterAboutSite(location.href)}/`,
          patchObject
        );
        const temporaryUpdatedScheduleData = temporaryUpdateScheduleData(
          response.data
        );
        checkIfSyncNeeded(navigate);
        setTimeRanges(
          handleCreateInitialTimeSchedules(
            temporaryUpdatedScheduleData[0].schedule
          )
        );
        setGrid(
          handleConvertSchedulesToBooleanArrays(
            temporaryUpdatedScheduleData[0].schedule
          )
        );
        setDuskToDawn(
          handleCreateDuskToDawn(temporaryUpdatedScheduleData[0].schedule)
        );
        setDuskToDawnLocation(
          handleCreateDuskToDawnLocationPlusBuffer(
            temporaryUpdatedScheduleData[0].schedule
          )
        );
        setSiteSchedules(temporaryUpdatedScheduleData);
        setBackupSiteSchedules(temporaryUpdatedScheduleData);
        localStorage.setItem(
          "backUpStandardSchedule",
          JSON.stringify(temporaryUpdatedScheduleData)
        );
        setEditMode(false);
        setScheduleGridEditable(false);
        const updatedSiteInfo = { ...siteInfo, has_flex_schedule: false };
        setActiveTab(<SchedulesTab siteInfo={updatedSiteInfo} />);
        setBackdropLoadingScreenOnEditingSchedule(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Trouble updating schedule. Please try again.");
            setBackdropLoadingScreenOnEditingSchedule(false);
          }
        );
      }
    } else {
      const postObjectFlex = siteSchedules;
      postObjectFlex[schedulesIndex].schedule = patchObject;

      if (await blockOnFlexPresetConflicts(siteInfo, postObjectFlex)) {
        setBackdropLoadingScreenOnEditingSchedule(false);
        return;
      }

      try {
        const response = await AxiosUI.patch(
          `/flex_schedule/by_customer/${getNumbersAfterAboutSite(
            location.href
          )}/`,
          postObjectFlex
        );
        await persistFlexSchedulePresetAssignmentsAfterPatch(
          siteInfo.id,
          response.data
        );
        checkIfSyncNeeded(navigate);
        setTimeRanges(
          handleCreateInitialTimeSchedules(
            response.data[schedulesIndex].schedule
          )
        );
        setGrid(
          handleConvertSchedulesToBooleanArrays(
            response.data[schedulesIndex].schedule
          )
        );
        setDuskToDawn(
          handleCreateDuskToDawn(response.data[schedulesIndex].schedule)
        );
        setDuskToDawnLocation(
          handleCreateDuskToDawnLocationPlusBuffer(
            response.data[schedulesIndex].schedule
          )
        );
        setSelectedDayInfo(response.data[schedulesIndex]);
        setBackdropLoadingScreenOnEditingSchedule(false);
        setSiteSchedules(response.data);
        setBackupSiteSchedules(response.data);
        localStorage.setItem(
          "backUpFlexSchedule",
          JSON.stringify(response.data)
        );
        setEditMode(false);
        setScheduleGridEditable(false);
        const updatedSiteInfo = { ...siteInfo, has_flex_schedule: true };
        setActiveTab(<SchedulesTab siteInfo={updatedSiteInfo} />);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Trouble updating schedule. Please try again.");
            setBackdropLoadingScreenOnEditingSchedule(false);
          }
        );
      }
    }
  },

  handleSubmitEmptySchedule: async (
    setEditMode: any,
    setActiveTab: any,
    siteInfo: any,
    navigate: NavigateFunction
  ) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().handleSubmitEmptySchedule(
        setEditMode,
        setActiveTab,
        siteInfo,
        navigate
      );
    };

    const {
      setTimeRanges,
      setGrid,
      setDuskToDawn,
      setDuskToDawnLocation,
      setVisibleSchedules,
      setSiteSchedules,
      setBackupSiteSchedules,
      setBackdropLoadingScreenOnEditingSchedule,
      setScheduleGridEditable,
      checkIfSyncNeeded,
    } = get();

    const emptySchedule = [
      {
        customer: getNumbersAfterAboutSite(location.href),
        start_time: null,
        end_time: "00:00:00",
        always_on: false,
        day_of_week: ["5", "6", "2", "4", "3", "1", "0"],
        schedule_status: true,
        is_override: false,
        location_dusk_dawn: null,
        enabled: true,
        override_start_date: null,
        override_end_date: null,
        buffer_time: null,
      },
    ];

    setBackdropLoadingScreenOnEditingSchedule(true);

    try {
      const response = await AxiosUI.patch(
        `schedule/${getNumbersAfterAboutSite(location.href)}/`,
        emptySchedule
      );
      checkIfSyncNeeded(navigate);
      const temporaryUpdatedScheduleData = temporaryUpdateScheduleData(
        response.data
      );

      setTimeRanges(
        handleCreateInitialTimeSchedules(
          temporaryUpdatedScheduleData[0].schedule
        )
      );
      setGrid(
        handleConvertSchedulesToBooleanArrays(
          temporaryUpdatedScheduleData[0].schedule
        )
      );
      setDuskToDawn(
        handleCreateDuskToDawn(temporaryUpdatedScheduleData[0].schedule)
      );
      setDuskToDawnLocation(
        handleCreateDuskToDawnLocationPlusBuffer(
          temporaryUpdatedScheduleData[0].schedule
        )
      );
      setVisibleSchedules({
        one: true,
        two: false,
        three: false,
      });
      localStorage.setItem(
        "siteSchedule",
        JSON.stringify(temporaryUpdatedScheduleData)
      );
      setSiteSchedules(temporaryUpdatedScheduleData);
      localStorage.setItem(
        "backUpStandardSchedule",
        JSON.stringify(temporaryUpdatedScheduleData)
      );
      setBackupSiteSchedules(temporaryUpdatedScheduleData);
      setBackdropLoadingScreenOnEditingSchedule(false);
      setEditMode(false);
      setScheduleGridEditable(false);
      setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
    } catch (error) {
      universalAPIErrorHandler(
        error as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Trouble updating schedule. Please try again.");
          const backupSchedules = JSON.parse(
            localStorage.getItem("backUpStandardSchedule") || "[]"
          );
          setSiteSchedules(backupSchedules);
          setGrid(
            handleConvertSchedulesToBooleanArrays(backupSchedules[0].schedule)
          );
          setDuskToDawn(handleCreateDuskToDawn(backupSchedules[0].schedule));
          setDuskToDawnLocation(
            handleCreateDuskToDawnLocationPlusBuffer(
              backupSchedules[0].schedule
            )
          );
          setTimeRanges(
            handleCreateInitialTimeSchedules(backupSchedules[0].schedule)
          );
          setVisibleSchedules({
            one: true,
            two: false,
            three: false,
          });
          localStorage.setItem("siteSchedule", JSON.stringify(backupSchedules));
          setEditMode(false);
          setScheduleGridEditable(false);
          setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
          setBackdropLoadingScreenOnEditingSchedule(false);
        }
      );
    }
  },

  handleFlexSchedulingToggle: async (
    enabled: boolean,
    setSiteInfo: any,
    setEditMode: any,
    setActiveTab: any,
    siteInfo: any,
    navigate: NavigateFunction
  ) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().handleFlexSchedulingToggle(
        enabled,
        setSiteInfo,
        setEditMode,
        setActiveTab,
        siteInfo,
        navigate
      );
    };

    const {
      setTimeRanges,
      setGrid,
      setDuskToDawn,
      setDuskToDawnLocation,
      setSelectedDayInfo,
      setVisibleSchedules,
      setSiteSchedules,
      setBackupSiteSchedules,
      schedulesIndex,
      setSchedulesIndex,
      setFlexSchedulesOn,
      setBackdropLoadingScreenOnEditingSchedule,
      getNonFlexSchedules,
      setScheduleGridEditable,
      checkIfSyncNeeded,
    } = get();

    setBackdropLoadingScreenOnEditingSchedule(true);

    try {
      if (enabled) {
        const response = await AxiosUI.post(
          `flex_schedule/enable/${getNumbersAfterAboutSite(location.href)}/`
        );
        localStorage.setItem("siteSchedule", JSON.stringify(response.data));
        setTimeRanges(
          handleCreateInitialTimeSchedules(
            response.data[schedulesIndex].schedule
          )
        );
        setGrid(
          handleConvertSchedulesToBooleanArrays(
            response.data[schedulesIndex].schedule
          )
        );
        setDuskToDawn(
          handleCreateDuskToDawn(response.data[schedulesIndex].schedule)
        );
        setDuskToDawnLocation(
          handleCreateDuskToDawnLocationPlusBuffer(
            response.data[schedulesIndex].schedule
          )
        );
        setSelectedDayInfo(response.data[schedulesIndex]);
        setVisibleSchedules({
          one: true,
          two: false,
          three: false,
        });
        setSiteSchedules(response.data);
        setBackupSiteSchedules(response.data);
        localStorage.setItem(
          "backUpFlexSchedule",
          JSON.stringify(response.data)
        );
        if (response.data.length === 1) {
          setVisibleSchedules({
            one: true,
            two: false,
            three: false,
          });
        }
        if (response.data.length === 2) {
          setVisibleSchedules({
            one: true,
            two: true,
            three: false,
          });
        }
        if (response.data.length === 3) {
          setVisibleSchedules({
            one: true,
            two: true,
            three: true,
          });
        }
        setSiteInfo((prev: any) => {
          return {
            ...prev,
            has_flex_schedule: true,
          };
        });
        setFlexSchedulesOn(true);
        setEditMode(false);
        setScheduleGridEditable(false);
      } else {
        await AxiosUI.post(
          `flex_schedule/disable/${getNumbersAfterAboutSite(location.href)}/`
        );
        setFlexSchedulesOn(false);
        setSiteInfo((prev: any) => {
          return {
            ...prev,
            has_flex_schedule: false,
          };
        });
        setEditMode(false);
        setScheduleGridEditable(false);
        setSchedulesIndex(0); // Reset to first schedule
        await getNonFlexSchedules(
          0,
          () => {},
          () => {},
          navigate
        );
        checkIfSyncNeeded(navigate);
        // Get the updated siteInfo from the store and set active tab after non-flex schedules are loaded
        const updatedSiteInfo = { ...siteInfo, has_flex_schedule: false };
        setActiveTab(<SchedulesTab siteInfo={updatedSiteInfo} />);
      }

      // Turn off loading screen after both operations complete
      if (enabled) {
        checkIfSyncNeeded(navigate);
        const updatedSiteInfo = { ...siteInfo, has_flex_schedule: true };
        setActiveTab(<SchedulesTab siteInfo={updatedSiteInfo} />);
      }
      setBackdropLoadingScreenOnEditingSchedule(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert(
            `Problem ${enabled ? "enabling" : "disabling"} flex scheduling.`
          );
          setBackdropLoadingScreenOnEditingSchedule(false);
        }
      );
    }
  },

  handleAddSchedule: async (navigate: NavigateFunction) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().handleAddSchedule(navigate);
    };

    const {
      setSiteSchedules,
      setBackupSiteSchedules,
      siteSchedules,
      setSchedulesIndex,
      setTimeRanges,
      setGrid,
      setDuskToDawn,
      setDuskToDawnLocation,
      setSelectedDayInfo,
      setClickedSchedulesTab,
      setBackdropLoadingScreenOnEditingSchedule,
      checkIfSyncNeeded,
    } = get();

    setBackdropLoadingScreenOnEditingSchedule(true);

    try {
      const response = await AxiosUI.post(
        `flex_schedule/add/${getNumbersAfterAboutSite(location.href)}/`
      );

      const newScheduleIndex = findNewScheduleIndex(
        siteSchedules,
        response.data
      );

      setSchedulesIndex(newScheduleIndex);

      setTimeRanges(
        handleCreateInitialTimeSchedules(
          response.data[newScheduleIndex].schedule
        )
      );
      setGrid(
        handleConvertSchedulesToBooleanArrays(
          response.data[newScheduleIndex].schedule
        )
      );
      setDuskToDawn(
        handleCreateDuskToDawn(response.data[newScheduleIndex].schedule)
      );
      setDuskToDawnLocation(
        handleCreateDuskToDawnLocationPlusBuffer(
          response.data[newScheduleIndex].schedule
        )
      );
      setSelectedDayInfo(response.data[newScheduleIndex]);

      if (newScheduleIndex === 0) {
        setClickedSchedulesTab("one");
      }

      if (newScheduleIndex === 1) {
        setClickedSchedulesTab("two");
      }

      if (newScheduleIndex === 2) {
        setClickedSchedulesTab("three");
      }

      const updatedSiteSchedules = siteSchedules;

      updatedSiteSchedules.push(response.data[response.data.length - 1]);

      const updatedBackupFlexSchedule = JSON.parse(
        localStorage.backUpFlexSchedule
      );

      updatedBackupFlexSchedule.push(response.data[response.data.length - 1]);

      setSiteSchedules(updatedSiteSchedules);
      localStorage.setItem(
        "backUpFlexSchedule",
        JSON.stringify(updatedBackupFlexSchedule)
      );
      setBackdropLoadingScreenOnEditingSchedule(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Problem adding schedule. Please try again.");
          setBackdropLoadingScreenOnEditingSchedule(false);
        }
      );
    }
  },

  handleDeleteSchedule: async (
    scheduleId: string,
    navigate: NavigateFunction
  ) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().handleDeleteSchedule(scheduleId, navigate);
    };

    const {
      siteSchedules,
      setSiteSchedules,
      setBackupSiteSchedules,
      setSchedulesIndex,
      setClickedSchedulesTab,
      setBackdropLoadingScreenOnEditingSchedule,
      setTimeRanges,
      setGrid,
      setDuskToDawn,
      setDuskToDawnLocation,
      setSelectedDayInfo,
    } = get();

    setBackdropLoadingScreenOnEditingSchedule(true);

    try {
      await AxiosUI.delete(`flex_schedule/${scheduleId}/`);

      const filteredSiteSchedules = siteSchedules.filter(
        (schedule: any) => schedule.id !== scheduleId
      );

      setSchedulesIndex(0);
      setClickedSchedulesTab("one");

      setTimeRanges(
        handleCreateInitialTimeSchedules(siteSchedules[0].schedule)
      );
      setGrid(handleConvertSchedulesToBooleanArrays(siteSchedules[0].schedule));
      setDuskToDawn(handleCreateDuskToDawn(siteSchedules[0].schedule));
      setDuskToDawnLocation(
        handleCreateDuskToDawnLocationPlusBuffer(siteSchedules[0].schedule)
      );
      setSelectedDayInfo(siteSchedules[0]);

      setSiteSchedules(filteredSiteSchedules);
      setBackupSiteSchedules(filteredSiteSchedules);
      localStorage.setItem(
        "backUpFlexSchedule",
        JSON.stringify(filteredSiteSchedules)
      );

      setBackdropLoadingScreenOnEditingSchedule(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Problem deleting schedule. Please try again.");
          setBackdropLoadingScreenOnEditingSchedule(false);
        }
      );
    }
  },

  schedulesIndex: 0,
  setSchedulesIndex: (schedulesIndex: any) => set({ schedulesIndex }),

  timeRanges: [],
  setTimeRanges: (timeRanges: any) => set({ timeRanges }),

  grid: [],
  setGrid: (grid: any) => set({ grid }),

  duskToDawn: [],
  setDuskToDawn: (duskToDawn: any) => set({ duskToDawn }),

  duskToDawnLocation: [],
  setDuskToDawnLocation: (duskToDawnLocation: any) =>
    set({ duskToDawnLocation }),

  clickedSchedulesTab: "one",
  setClickedSchedulesTab: (clickedSchedulesTab: any) =>
    set({ clickedSchedulesTab }),

  visibleSchedules: [],
  setVisibleSchedules: (visibleSchedules: any) => set({ visibleSchedules }),

  scheduleGridEditable: true,
  setScheduleGridEditable: (scheduleGridEditable: boolean) =>
    set({ scheduleGridEditable }),

  scheduleChangesMade: false,
  setScheduleChangesMade: (scheduleChangesMade: boolean) =>
    set({ scheduleChangesMade }),

  selectedDayInfo: {},
  setSelectedDayInfo: (selectedDayInfo: any) => set({ selectedDayInfo }),

  matchingSchedules: [],
  setMatchingSchedules: (matchingSchedules: number[][]) =>
    set({ matchingSchedules }),

  flexSchedulesOn: false,
  setFlexSchedulesOn: (flexSchedulesOn: boolean) => set({ flexSchedulesOn }),

  editTimeScheduleDialogOpen: false,
  setEditTimeScheduleDialogOpen: (editTimeScheduleDialogOpen: boolean) =>
    set({ editTimeScheduleDialogOpen }),

  editActiveProductsDialogOpen: false,
  setEditActiveProductsDialogOpen: (editActiveProductsDialogOpen: boolean) =>
    set({ editActiveProductsDialogOpen }),

  editScheduleNameDialogOpen: false,
  setEditScheduleNameDialogOpen: (editScheduleNameDialogOpen: boolean) =>
    set({ editScheduleNameDialogOpen }),

  deleteScheduleDialogOpen: false,
  setDeleteScheduleDialogOpen: (deleteScheduleDialogOpen: boolean) =>
    set({ deleteScheduleDialogOpen }),

  deactivateSiteInsteadDialogOpen: false,
  setDeactivateSiteInsteadDialogOpen: (
    deactivateSiteInsteadDialogOpen: boolean
  ) => set({ deactivateSiteInsteadDialogOpen }),

  schedulesBackdropLoadingScreenOn: false,
  setSchedulesBackdropLoadingScreenOn: (
    schedulesBackdropLoadingScreenOn: boolean
  ) => set({ schedulesBackdropLoadingScreenOn }),

  healthCheckDataZustand: {},
  setHealthCheckDataZustand: (healthCheckDataZustand: any) =>
    set({ healthCheckDataZustand }),

  autoPatrolSchedules: [],
  setAutoPatrolSchedules: (autoPatrolSchedules: AutoPatrolSchedule[]) =>
    set({ autoPatrolSchedules }),

  selectedAutoPatrolScheduleIndex: 0,
  setSelectedAutoPatrolScheduleIndex: (
    selectedAutoPatrolScheduleIndex: number
  ) => set({ selectedAutoPatrolScheduleIndex }),
  autoPatrolFlexIgnoreZonesEnabledBySite: {},
  setAutoPatrolFlexIgnoreZonesEnabledForSite: (
    siteId: number,
    enabled: boolean
  ) =>
    set((state) => ({
      autoPatrolFlexIgnoreZonesEnabledBySite: {
        ...state.autoPatrolFlexIgnoreZonesEnabledBySite,
        [siteId]: enabled,
      },
    })),
  autoPatrolFlexIgnoreZonesWizardOpenBySite: {},
  setAutoPatrolFlexIgnoreZonesWizardOpenForSite: (
    siteId: number,
    open: boolean
  ) =>
    set((state) => ({
      autoPatrolFlexIgnoreZonesWizardOpenBySite: {
        ...state.autoPatrolFlexIgnoreZonesWizardOpenBySite,
        [siteId]: open,
      },
    })),
  autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite: {},
  setAutoPatrolFlexIgnoreZonesActiveScheduleIndexForSite: (
    siteId: number,
    index: number
  ) =>
    set((state) => ({
      autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite: {
        ...state.autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite,
        [siteId]: index,
      },
    })),
  autoPatrolFlexIgnoreZonesPresetsByCamera: {},
  setAutoPatrolFlexIgnoreZonesPresetsForCamera: (
    cameraId: number,
    presets: AutoPatrolFlexIgnoreZonesPreset[]
  ) =>
    set((state) => ({
      autoPatrolFlexIgnoreZonesPresetsByCamera: {
        ...state.autoPatrolFlexIgnoreZonesPresetsByCamera,
        [cameraId]: presets,
      },
    })),
  autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera: {},
  setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera: (
    scheduleId: number,
    cameraId: number,
    presetLocalId: string
  ) =>
    set((state) => ({
      autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera: {
        ...state.autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera,
        [`${scheduleId}:${cameraId}`]: presetLocalId,
      },
    })),
  autoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule: {},
  setAutoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule: (
    scheduleId: number,
    cameraIds: number[]
  ) =>
    set((state) => ({
      autoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule: {
        ...state.autoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule,
        [scheduleId]: cameraIds,
      },
    })),
  initializeAutoPatrolFlexIgnoreZonesForSite: ({
    siteId,
    scheduleIds,
    cameraIdsBySchedule,
  }) =>
    set((state) => {
      const selectedPresetByScheduleCamera = {
        ...state.autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera,
      };
      scheduleIds.forEach((scheduleId) => {
        const cameraIds = cameraIdsBySchedule[scheduleId] || [];
        cameraIds.forEach((cameraId) => {
          const key = `${scheduleId}:${cameraId}`;
          if (!selectedPresetByScheduleCamera[key]) {
            selectedPresetByScheduleCamera[key] = `default-${cameraId}`;
          }
        });
      });
      return {
        autoPatrolFlexIgnoreZonesEnabledBySite: {
          ...state.autoPatrolFlexIgnoreZonesEnabledBySite,
          [siteId]:
            state.autoPatrolFlexIgnoreZonesEnabledBySite[siteId] ?? false,
        },
        autoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule: {
          ...state.autoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule,
          ...cameraIdsBySchedule,
        },
        autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera:
          selectedPresetByScheduleCamera,
      };
    }),
  resetAutoPatrolFlexIgnoreZonesForSite: (siteId: number) =>
    set((state) => {
      const nextEnabled = { ...state.autoPatrolFlexIgnoreZonesEnabledBySite };
      const nextOpen = { ...state.autoPatrolFlexIgnoreZonesWizardOpenBySite };
      const nextActiveIndex = {
        ...state.autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite,
      };
      delete nextEnabled[siteId];
      delete nextOpen[siteId];
      delete nextActiveIndex[siteId];
      return {
        autoPatrolFlexIgnoreZonesEnabledBySite: nextEnabled,
        autoPatrolFlexIgnoreZonesWizardOpenBySite: nextOpen,
        autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite: nextActiveIndex,
        autoPatrolFlexIgnoreZonesPresetsByCamera: {},
        autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera: {},
        autoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule: {},
      };
    }),

  autopatrolCamerasTablePage: 0,
  setAutopatrolCamerasTablePage: (autopatrolCamerasTablePage: number) =>
    set({ autopatrolCamerasTablePage }),
  autopatrolCamerasTableRowsPerPage: 5,
  setAutopatrolCamerasTableRowsPerPage: (
    autopatrolCamerasTableRowsPerPage: number
  ) => set({ autopatrolCamerasTableRowsPerPage }),

  autoPatrolSchedulesUpdatedBoolean: false,
  setAutoPatrolSchedulesUpdatedBoolean: (
    autoPatrolSchedulesUpdatedBoolean: boolean
  ) => set({ autoPatrolSchedulesUpdatedBoolean }),

  updatedAutoPatrolScheduleIndexes: [],
  setUpdatedAutoPatrolScheduleIndexes: (
    updatedAutoPatrolScheduleIndexes: string[]
  ) => set({ updatedAutoPatrolScheduleIndexes }),

  autoPatrolScheduleHealthchckMetrics: {},
  setAutoPatrolScheduleHealthchckMetrics: (
    autoPatrolScheduleHealthchckMetrics: any
  ) => set({ autoPatrolScheduleHealthchckMetrics }),

  autoPatrolScheduleHealthchckMetricsUpdatedBoolean: false,
  setAutoPatrolScheduleHealthchckMetricsUpdatedBoolean: (
    autoPatrolScheduleHealthchckMetricsUpdatedBoolean: boolean
  ) => set({ autoPatrolScheduleHealthchckMetricsUpdatedBoolean }),

  updatedAutoPatrolScheduleHealthchckMetricsIds: [],
  setUpdatedAutoPatrolScheduleHealthchckMetricsIds: (
    updatedAutoPatrolScheduleHealthchckMetricsIds: number[]
  ) => set({ updatedAutoPatrolScheduleHealthchckMetricsIds }),

  getAutoPatrolScheduleHealthchckMetrics: async (
    navigate,
    scheduleId,
    finalIndex: boolean
  ) => {
    const {
      setAutoPatrolScheduleHealthchckMetrics,
      autoPatrolScheduleHealthchckMetrics,
    } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getAutoPatrolScheduleHealthchckMetrics(
        navigate,
        scheduleId,
        finalIndex
      );
    };
    try {
      const response = await AxiosUI.get(
        `auto_patrol_metric_healthcheck/by_schedule/${scheduleId}/`
      );
      const arranged = arrangeScheduleHealthMetricsAutopatrol(response.data);
      const updatedMetrics = {
        ...autoPatrolScheduleHealthchckMetrics,
        [scheduleId]: arranged,
      };
      // vCHOnlySchedules is now determined in getAutoPatrolSchedules using patrolType
      setAutoPatrolScheduleHealthchckMetrics(updatedMetrics);
      localStorage.setItem(
        "backupAutoPatrolScheduleHealthchckMetrics",
        JSON.stringify(updatedMetrics)
      );
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  patchAutoPatrolScheduleHealthchckMetrics: async (
    navigate,
    id,
    activeBoolean,
    finalIndex,
    minimumResolutionHeight
  ) => {
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().patchAutoPatrolScheduleHealthchckMetrics(
        navigate,
        id,
        activeBoolean,
        finalIndex,
        minimumResolutionHeight
      );
    };

    const {
      setBackdropLoadingScreenOnEditingSchedule,
      setAutoPatrolScheduleHealthchckMetricsUpdatedBoolean,
      setUpdatedAutoPatrolScheduleHealthchckMetricsIds,
      setAutoPatrolScheduleHealthchckMetrics,
    } = get();

    setBackdropLoadingScreenOnEditingSchedule(true);

    try {
      await AxiosUI.patch(`auto_patrol_metric_healthcheck/${id}/`, {
        enabled: activeBoolean,
        minimum_resolution_height: minimumResolutionHeight,
      });
      if (finalIndex) {
        setBackdropLoadingScreenOnEditingSchedule(false);
        setAutoPatrolScheduleHealthchckMetricsUpdatedBoolean(false);
        setUpdatedAutoPatrolScheduleHealthchckMetricsIds([]);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert(
            "Problem updating auto patrol schedule health check metrics. Please try again."
          );
          setAutoPatrolScheduleHealthchckMetricsUpdatedBoolean(false);
          setUpdatedAutoPatrolScheduleHealthchckMetricsIds([]);
          setAutoPatrolScheduleHealthchckMetrics(
            JSON.parse(localStorage.backupAutoPatrolScheduleHealthchckMetrics)
          );
          setBackdropLoadingScreenOnEditingSchedule(false);
        }
      );
    }
  },

  getAutoPatrolSchedules: async (navigate, siteId) => {
    const {
      setAutoPatrolSchedules,
      getAutoPatrolScheduleHealthchckMetrics,
      getAutoPatrolCameraProducts,
    } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getAutoPatrolSchedules(navigate, siteId);
    };

    try {
      const response = await AxiosUI.get(
        `auto_patrol_schedule/by_customer/${siteId}/`
      );
      // Normalize: API may return an array or { results: [...] }
      const rawData = response.data;
      const schedulesArray = Array.isArray(rawData)
        ? rawData
        : rawData?.results ?? [];
      // Filter out deleted schedules
      const activeSchedules = schedulesArray.filter(
        (schedule: AutoPatrolSchedule) => schedule.scheduleStatus !== "Deleted"
      );
      setAutoPatrolSchedules(activeSchedules);
      localStorage.setItem(
        "backupAutoPatrolSchedules",
        JSON.stringify(activeSchedules)
      );

      // Determine if this is a VCH-only site (no AutoPatrol schedules)
      const hasAPSchedules = activeSchedules.some(
        (schedule: AutoPatrolSchedule) => schedule.patrolType === "AutoPatrol"
      );
      get().setvCHOnlySchedules(!hasAPSchedules);

      activeSchedules.forEach((schedule: AutoPatrolSchedule, index: number) => {
        getAutoPatrolScheduleHealthchckMetrics(
          navigate,
          schedule.id.toString(),
          activeSchedules.length - 1 === index
        );
        if (schedule.patrolType === "AutoPatrol") {
          getAutoPatrolCameraProducts(navigate, schedule.id);
        }
      });
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setAutoPatrolSchedules([]); // Reset to empty array on error
        }
      );
    }
  },

  patchAutoPatrolSchedules: async (navigate, index) => {
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().patchAutoPatrolSchedules(navigate, index);
    };

    const {
      autoPatrolSchedules,
      setAutoPatrolSchedules,
      setBackdropLoadingScreenOnEditingSchedule,
      updatedAutoPatrolScheduleIndexes,
      setAutoPatrolSchedulesUpdatedBoolean,
      setUpdatedAutoPatrolScheduleIndexes,
    } = get();

    setBackdropLoadingScreenOnEditingSchedule(true);

    const submittedAutoPatrolSchedule = autoPatrolSchedules[index];

    try {
      await AxiosUI.patch(
        `auto_patrol_schedule/${submittedAutoPatrolSchedule.id}/`,
        { sensitivityLevel: submittedAutoPatrolSchedule.sensitivityLevel }
      );
      setAutoPatrolSchedulesUpdatedBoolean(false);
      setUpdatedAutoPatrolScheduleIndexes([]);
      if (Number(index) === updatedAutoPatrolScheduleIndexes.length - 1) {
        setBackdropLoadingScreenOnEditingSchedule(false);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert(
            "Problem updating auto patrol schedule sensitivity. Please try again."
          );
          setAutoPatrolSchedules(
            JSON.parse(localStorage.backupAutoPatrolSchedules)
          );
          setAutoPatrolSchedulesUpdatedBoolean(false);
          setUpdatedAutoPatrolScheduleIndexes([]);
          setBackdropLoadingScreenOnEditingSchedule(false);
        }
      );
    }
  },

  syncSiteNeeded: false,
  setSyncSiteNeeded: (syncSiteNeeded: boolean) => set({ syncSiteNeeded }),

  syncPending: false,
  setSyncPending: (syncPending: boolean) => set({ syncPending }),

  syncSiteNeededAlertOpen: false,
  setSyncSiteNeededAlertOpen: (syncSiteNeededAlertOpen: boolean) =>
    set({ syncSiteNeededAlertOpen }),

  checkIfSyncNeeded: async (navigate: NavigateFunction) => {
    const AxiosUI = createApi(``);

    const { setSyncSiteNeeded, setSyncSiteNeededAlertOpen } = get();

    const retryFunction = () => {
      get().checkIfSyncNeeded(navigate);
    };

    try {
      const response = await AxiosUI.get(
        `customer/${getNumbersAfterAboutSite(location.href)}/about/`
      );
      if (
        response.data.settings_deployed === false &&
        response.data.integration_type_name !== "clips"
      ) {
        setSyncSiteNeeded(true);
        setSyncSiteNeededAlertOpen(true);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  resetSiteAboutState: () =>
    set({
      siteInfo: {} as SiteInfo,
      smtpInstructions: "",
      hideUseMotion: false,
      siteAboutAlerts: {
        scheduleSubmittedAndSiteSynced: false,
      },
      healthCheckMetricsData: {
        connectivity_check: {
          id: 0,
          enabled: false,
          alert_emails: "",
        },
        motion_status_check: {
          id: 0,
          enabled: false,
          alert_emails: "",
        },
        recording_check: {
          id: 0,
          enabled: false,
          alert_emails: "",
        },
        scene_change_check: {
          id: 0,
          enabled: false,
          alert_emails: "",
        },
        stream_quality_check: {
          id: 0,
          enabled: false,
          alert_emails: "",
        },
        image_quality_check: {
          id: 0,
          enabled: false,
          alert_emails: "",
        },
      },
      siteSchedules: [],
      schedulesIndex: 0,
      timeRanges: [],
      grid: [],
      duskToDawn: [],
      duskToDawnLocation: [],
      clickedSchedulesTab: "one",
      visibleSchedules: [],
      scheduleGridEditable: false,
      autoPatrolSchedules: [],
      selectedAutoPatrolScheduleIndex: 0,
      autoPatrolFlexIgnoreZonesEnabledBySite: {},
      autoPatrolFlexIgnoreZonesWizardOpenBySite: {},
      autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite: {},
      autoPatrolFlexIgnoreZonesPresetsByCamera: {},
      autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera: {},
      autoPatrolFlexIgnoreZonesSelectedCameraIdsBySchedule: {},
      autopatrolCamerasTablePage: 0,
      autopatrolCamerasTableRowsPerPage: 5,
      vmsVariables: [],
      generalInfoTabPatchObject: {},
      generalInfoTabChangesMade: false,
      backdropLoadingScreenOnEditingSchedule: false,
      syncSiteNeeded: false,
      syncPending: false,
      autoPatrolScheduleHealthchckMetrics: {},
      autoPatrolCameraProducts: {},
      updatedProductMetricsIds: {},
      productMetricsWereUpdated: false,
      lineCrossingsData: {},
      presetLineCrossingsData: {},
      lineCrossingsMetricLabels: [],
      activeCameraProducts: {},
      chmOnlyIgnoreZonesByCamera: {},
      cameraStatusById: {},
      ignoreZonesByCamera: {},
      metadataTagZonesByCamera: {},
      rowPrefetchStateByCamera: {},
      activeCameraProductsLoading: {},
      cameraSlice: {},
      cameraIdToStreamIds: {},
      siteMetricsSimplified: [],
      siteMetricsSimplifiedCustomerId: null,
    }),

  handleUpdateTimeSchedulesForAddSchedule: (
    dayOfWeekArray: number[],
    startTime: string,
    endTime: string,
    alwaysOn: boolean,
    duskToDawn: boolean,
    bufferTime: number,
    duskToDawnLocation: string
  ) => {
    const {
      siteSchedules,
      timeRanges,
      selectedDayInfo,
      duskToDawnLocation: currentDuskToDawnLocation,
      setSiteSchedules,
      setTimeRanges,
      setGrid,
      setMatchingSchedules,
      setSelectedDayInfo,
      setDuskToDawnLocation,
    } = get();

    const filterArrays = (arr1: number[], arr2: number[]): number[] => {
      // Add null checks to prevent the filter error
      if (!arr1 || !Array.isArray(arr1)) return [];
      if (!arr2 || !Array.isArray(arr2)) return arr1;
      return arr1.filter((num) => !arr2.includes(num));
    };

    const updatedStartTime = duskToDawn ? "17:00:00" : startTime;
    const updatedEndTime = duskToDawn ? "08:00:00" : endTime;

    let timeRangeString = "";

    if (!duskToDawn && !alwaysOn) {
      timeRangeString = `${updatedStartTime.slice(
        0,
        5
      )} - ${updatedEndTime.slice(0, 5)}`;
    }

    if (duskToDawn) {
      timeRangeString = "Dusk to Dawn";
    }

    if (alwaysOn) {
      timeRangeString = "Always On";
    }

    const newSiteScheduleObject = {
      always_on: alwaysOn,
      buffer_time: Number(bufferTime),
      customer: Number(getNumbersAfterAboutSite(location.href)),
      day_of_week: dayOfWeekArray,
      enabled: true,
      end_time: updatedEndTime,
      is_overide: false,
      location_dusk_dawn: duskToDawn,
      start_time: updatedStartTime,
    };

    const updatedSiteSchedules = [...(siteSchedules || [])];

    // Only process existing schedules if they have the expected structure
    updatedSiteSchedules.forEach((siteSchedule: any, index: number) => {
      if (
        siteSchedule &&
        siteSchedule.day_of_week &&
        Array.isArray(siteSchedule.day_of_week)
      ) {
        updatedSiteSchedules[index].day_of_week = filterArrays(
          siteSchedule.day_of_week,
          dayOfWeekArray
        );
      } else {
        // If the schedule doesn't have the expected structure, remove it
        updatedSiteSchedules.splice(index, 1);
      }
    });

    updatedSiteSchedules.push(newSiteScheduleObject);

    const updatedTimeRanges = [...(timeRanges || [])];

    updatedTimeRanges.forEach((timeRange, index) => {
      if (dayOfWeekArray.includes(index)) {
        updatedTimeRanges[index] = timeRangeString;
      }
    });

    // Update duskToDawnLocation array for the affected days
    const updatedDuskToDawnLocation = [...(currentDuskToDawnLocation || [])];
    dayOfWeekArray.forEach((day) => {
      if (updatedDuskToDawnLocation[day]) {
        updatedDuskToDawnLocation[day] = [duskToDawn, bufferTime];
      }
    });

    const updatedMatchingSchedules: number[][] = [];

    updatedSiteSchedules.forEach((siteSchedule: any) => {
      if (
        siteSchedule &&
        siteSchedule.day_of_week &&
        Array.isArray(siteSchedule.day_of_week)
      ) {
        updatedMatchingSchedules.push(siteSchedule.day_of_week);
      }
    });

    // Check the number arrays in updatedMatchingSchedules, and if between all of them any of the numbers 0 through 6 are missing, create a new array with those numbers and push it to updatedMatchingSchedules
    const missingDays: number[] = [];
    for (let i = 0; i < 7; i++) {
      let found = false;
      updatedMatchingSchedules.forEach((arr) => {
        if (arr && arr.includes(i)) {
          found = true;
        }
      });
      if (!found) {
        missingDays.push(i);
      }
    }

    if (missingDays.length > 0) {
      updatedMatchingSchedules.push(missingDays);
    }

    const finalSiteSchedulesArray: any[] = [];

    updatedSiteSchedules.forEach((siteSchedule: any) => {
      if (
        siteSchedule &&
        siteSchedule.day_of_week &&
        Array.isArray(siteSchedule.day_of_week) &&
        siteSchedule.day_of_week.length > 0
      ) {
        finalSiteSchedulesArray.push(siteSchedule);
      }
    });

    // Update selectedDayInfo with the new values
    const updatedSelectedDayInfo = {
      ...selectedDayInfo,
      timeRanges: timeRangeString,
      duskToDawn: duskToDawn,
      bufferTime: bufferTime,
      day: selectedDayInfo.day, // Keep the current day
    };

    setMatchingSchedules(updatedMatchingSchedules);
    setSiteSchedules(finalSiteSchedulesArray);
    setTimeRanges(updatedTimeRanges);
    setDuskToDawnLocation(updatedDuskToDawnLocation);
    setGrid(handleConvertSchedulesToBooleanArrays(finalSiteSchedulesArray));
    setSelectedDayInfo(updatedSelectedDayInfo);
  },

  addSiteWebhookDialogOpen: false,
  setAddSiteWebhookDialogOpen: (addSiteWebhookDialogOpen) =>
    set({ addSiteWebhookDialogOpen }),
  editSiteWebhookId: null,
  setEditSiteWebhookId: (editSiteWebhookId) => set({ editSiteWebhookId }),
  deleteSiteWebhookId: null,
  setDeleteSiteWebhookId: (deleteSiteWebhookId) => set({ deleteSiteWebhookId }),

  backupWebhookData: [],
  setBackupWebhookData: (backupWebhookData) => set({ backupWebhookData }),

  metadataTagZones: [],
  setMetadataTagZones: (metadataTagZones: MetadataTagZones) =>
    set({ metadataTagZones }),

  getMetadataTagZones: async (navigate: NavigateFunction) => {
    const { setMetadataTagZones, getMetadataTagZones } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      getMetadataTagZones(navigate);
    };
    try {
      const response = await AxiosUI.get(
        `tagzone/?camera=${getNumbersAfterEditCamera(location.href)}`
      );
      setMetadataTagZones(response.data.results);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  autoPatrolCameraProducts: {} as {
    [key: number]: Array<{
      id: number;
      schedule: number;
      metric_label: {
        id: number;
        name: string;
        display_name: string;
        order: number | null;
        product: {
          id: number;
          name: string;
          value: string;
          order: number | null;
        };
      };
      enabled: boolean;
    }>;
  },
  setAutoPatrolCameraProducts: (autoPatrolCameraProducts: {
    [key: number]: Array<{
      id: number;
      schedule: number;
      metric_label: {
        id: number;
        name: string;
        display_name: string;
        order: number | null;
        product: {
          id: number;
          name: string;
          value: string;
          order: number | null;
        };
      };
      enabled: boolean;
    }>;
  }) => set({ autoPatrolCameraProducts }),

  getAutoPatrolCameraProducts: async (
    navigate: NavigateFunction,
    scheduleId: number
  ) => {
    const { setAutoPatrolCameraProducts } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getAutoPatrolCameraProducts(navigate, scheduleId);
    };
    try {
      const response = await AxiosUI.get(
        `auto_patrol_product_metric/?schedule=${scheduleId}`
      );
      // Read the latest state right before updating to avoid race conditions
      const currentState = get().autoPatrolCameraProducts;

      setAutoPatrolCameraProducts({
        ...currentState,
        [scheduleId]: response.data.results,
      });
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  activeCameraProducts: {} as {
    [cameraId: number]: { [productName: string]: boolean };
  },
  setActiveCameraProducts: (activeCameraProducts: {
    [cameraId: number]: { [productName: string]: boolean };
  }) => set({ activeCameraProducts }),
  activeCameraProductsLoading: {} as { [cameraId: number]: true },
  setActiveCameraProductsLoading: (loading: { [cameraId: number]: true }) =>
    set({ activeCameraProductsLoading: loading }),
  setActiveCameraProductsForCamera: (
    cameraId: number,
    products: Record<string, boolean>
  ) => {
    const { activeCameraProducts, setActiveCameraProducts } = get();
    setActiveCameraProducts({
      ...activeCameraProducts,
      [cameraId]: products,
    });
  },
  chmOnlyIgnoreZonesByCamera: {} as { [cameraId: number]: boolean },
  setChmOnlyIgnoreZonesByCamera: (map: { [cameraId: number]: boolean }) =>
    set({ chmOnlyIgnoreZonesByCamera: map }),
  setChmOnlyIgnoreZonesForCamera: (cameraId: number, isChmOnly: boolean) => {
    const { chmOnlyIgnoreZonesByCamera, setChmOnlyIgnoreZonesByCamera } = get();
    setChmOnlyIgnoreZonesByCamera({
      ...chmOnlyIgnoreZonesByCamera,
      [cameraId]: isChmOnly,
    });
  },
  getActiveCameraProductsForCamera: async (
    navigate: NavigateFunction,
    cameraId: number
  ) => {
    const state = get();
    if (state.activeCameraProducts[cameraId] !== undefined) return;
    if (state.activeCameraProductsLoading[cameraId]) return;
    state.setActiveCameraProductsLoading({
      ...state.activeCameraProductsLoading,
      [cameraId]: true as const,
    });
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getActiveCameraProductsForCamera(navigate, cameraId);
    };
    try {
      const response = await AxiosUI.get(`/camera/${cameraId}/general_info/`);
      const streams = response.data?.streams;
      const derived = deriveActiveProductsFromGeneralInfo(streams);
      get().setActiveCameraProductsForCamera(cameraId, derived);
      const slice =
        Array.isArray(streams) &&
        streams.some((s: { slice?: boolean }) => s.slice === true);
      get().setCameraSliceForCamera(cameraId, !!slice);
      if (Array.isArray(streams)) {
        const ids = streams
          .map((s: { id?: number }) => s.id)
          .filter(
            (id: number | undefined): id is number => typeof id === "number"
          );
        get().setCameraStreamIdsForCamera(cameraId, ids);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    } finally {
      const s = get();
      const next = { ...s.activeCameraProductsLoading };
      delete next[cameraId];
      s.setActiveCameraProductsLoading(next);
    }
  },
  getActiveCameraProductsForAllCameras: async (
    navigate: NavigateFunction,
    cameraIds: number[]
  ) => {
    const AxiosUI = createApi(``);
    const state = get();
    const toFetch = cameraIds.filter(
      (id) => state.activeCameraProducts[id] === undefined
    );
    if (toFetch.length === 0) return;
    const firstId = toFetch[0];
    const loadingEntries = Object.fromEntries(
      toFetch.map((id) => [id, true as const])
    );
    state.setActiveCameraProductsLoading({
      ...state.activeCameraProductsLoading,
      ...loadingEntries,
    });
    for (const cameraId of toFetch) {
      try {
        const response = await AxiosUI.get(`/camera/${cameraId}/general_info/`);
        const streams = response.data?.streams;
        const derived = deriveActiveProductsFromGeneralInfo(streams);
        get().setActiveCameraProductsForCamera(cameraId, derived);
        const slice =
          Array.isArray(streams) &&
          streams.some((s: { slice?: boolean }) => s.slice === true);
        get().setCameraSliceForCamera(cameraId, !!slice);
        if (Array.isArray(streams)) {
          const ids = streams
            .map((s: { id?: number }) => s.id)
            .filter(
              (id: number | undefined): id is number => typeof id === "number"
            );
          get().setCameraStreamIdsForCamera(cameraId, ids);
        }
        if (cameraId === firstId) {
          applyImmixSureviewSentinelFromGeneralInfoResponse(response.data);
        }
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          () => {
            get().getActiveCameraProductsForAllCameras(navigate, toFetch);
          },
          () => {}
        );
      } finally {
        const s = get();
        const next = { ...s.activeCameraProductsLoading };
        delete next[cameraId];
        s.setActiveCameraProductsLoading(next);
      }
    }
  },

  cameraIdToStreamIds: {} as { [cameraId: number]: number[] },
  setCameraStreamIdsForCamera: (cameraId: number, streamIds: number[]) => {
    const { cameraIdToStreamIds } = get();
    set({
      cameraIdToStreamIds: {
        ...cameraIdToStreamIds,
        [cameraId]: streamIds,
      },
    });
  },
  clearBulkCameraCachesForCameras: (cameraIds: number[]) => {
    const {
      activeCameraProducts,
      cameraSlice,
      cameraIdToStreamIds,
      setActiveCameraProducts,
      setCameraSlice,
    } = get();
    const nextProducts = { ...activeCameraProducts };
    const nextSlice = { ...cameraSlice };
    const nextStreams = { ...cameraIdToStreamIds };
    cameraIds.forEach((id) => {
      delete nextProducts[id];
      delete nextSlice[id];
      delete nextStreams[id];
    });
    setActiveCameraProducts(nextProducts);
    setCameraSlice(nextSlice);
    set({ cameraIdToStreamIds: nextStreams });
  },

  siteMetricsSimplified: [] as MetricSimplifiedRow[],
  siteMetricsSimplifiedCustomerId: null as number | null,
  setSiteMetricsSimplifiedData: (rows, customerId) =>
    set({
      siteMetricsSimplified: rows,
      siteMetricsSimplifiedCustomerId: customerId,
    }),
  prefetchSiteMetricsSimplified: async (navigate, customerId) => {
    if (customerId === null || customerId === undefined) {
      return;
    }
    const parsedCustomerId =
      typeof customerId === "number"
        ? customerId
        : parseInt(String(customerId), 10);
    if (!Number.isFinite(parsedCustomerId)) {
      return;
    }
    const state = get();
    if (
      state.siteMetricsSimplifiedCustomerId === parsedCustomerId &&
      state.siteMetricsSimplified.length > 0
    ) {
      return;
    }
    try {
      const rows = await fetchMetricSimplified(navigate, parsedCustomerId);
      set({
        siteMetricsSimplified: rows,
        siteMetricsSimplifiedCustomerId: parsedCustomerId,
      });
    } catch {
      // Bulk and onboarding flows have their own fallbacks if this prefetch fails.
    }
  },

  refreshActiveCameraProductsFromSimplifiedEndpoint: async (
    navigate,
    customerId,
    cameraIds
  ) => {
    const rows = await fetchMetricSimplified(navigate, customerId);
    const parsedCustomerId =
      typeof customerId === "number"
        ? customerId
        : parseInt(String(customerId), 10);
    set({
      siteMetricsSimplified: rows,
      siteMetricsSimplifiedCustomerId: Number.isFinite(parsedCustomerId)
        ? parsedCustomerId
        : null,
    });
    cameraIds.forEach((id) => {
      get().setActiveCameraProductsForCamera(
        id,
        activeProductsDisplayRecordFromSimplifiedRows(rows, id)
      );
    });
  },

  prefetchBulkDialogActiveCameraProducts: async (
    navigate,
    customerId,
    cameraIds
  ) => {
    if (cameraIds.length === 0) {
      return;
    }
    const loadingEntries = Object.fromEntries(
      cameraIds.map((id) => [id, true as const])
    );
    const state = get();
    state.setActiveCameraProductsLoading({
      ...state.activeCameraProductsLoading,
      ...loadingEntries,
    });

    if (customerId !== undefined && customerId !== null) {
      try {
        const rows = await fetchMetricSimplified(navigate, customerId);
        set({
          siteMetricsSimplified: rows,
          siteMetricsSimplifiedCustomerId: customerId,
        });
        cameraIds.forEach((camId) => {
          const hasRow = rows.some((r) => Number(r.camera?.id) === camId);
          if (hasRow) {
            get().setActiveCameraProductsForCamera(
              camId,
              activeProductsDisplayRecordFromSimplifiedRows(rows, camId)
            );
            const nextLoading = { ...get().activeCameraProductsLoading };
            delete nextLoading[camId];
            get().setActiveCameraProductsLoading(nextLoading);
          }
        });
      } catch {
        // Fall through: general_info for cameras still missing below.
      }
    }

    const stillNeed = cameraIds.filter(
      (id) => get().activeCameraProducts[id] === undefined
    );
    if (stillNeed.length > 0) {
      await get().getActiveCameraProductsForAllCameras(navigate, stillNeed);
    }
  },

  cameraSlice: {} as { [cameraId: number]: boolean },
  setCameraSlice: (cameraSlice: { [cameraId: number]: boolean }) =>
    set({ cameraSlice }),
  setCameraSliceForCamera: (cameraId: number, slice: boolean) => {
    const { cameraSlice, setCameraSlice } = get();
    setCameraSlice({
      ...cameraSlice,
      [cameraId]: slice,
    });
  },

  updatedProductMetricsIds: {},
  setUpdatedProductMetricsIds: (updatedProductMetricsIds: {
    [key: number]: number[];
  }) => set({ updatedProductMetricsIds }),

  productMetricsWereUpdated: false,
  setProductMetricsWereUpdated: (productMetricsWereUpdated: boolean) =>
    set({ productMetricsWereUpdated }),

  updateScheduleProducts: async (
    productId: number,
    enabled: boolean,
    scheduleId: number,
    navigate: NavigateFunction
  ) => {
    const { autoPatrolCameraProducts, setAutoPatrolCameraProducts } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().updateScheduleProducts(productId, enabled, scheduleId, navigate);
    };

    try {
      await AxiosUI.patch(`auto_patrol_product_metric/${productId}/`, {
        enabled: enabled,
      });
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          // Revert the enabled value in autoPatrolCameraProducts on failure
          const scheduleProducts = autoPatrolCameraProducts[scheduleId] || [];
          const revertedProducts = scheduleProducts.map((p) =>
            p.id === productId ? { ...p, enabled: !enabled } : p
          );
          setAutoPatrolCameraProducts({
            ...autoPatrolCameraProducts,
            [scheduleId]: revertedProducts,
          });
          alert(
            "Problem updating schedule products for auto patrol schedule. Please try again."
          );
        }
      );
    }
  },
  lineCrossingsData: {},
  setLineCrossingsData: (lineCrossingsData: {
    [key: number]: LineCrossing[];
  }) => set({ lineCrossingsData }),

  lineCrossingsMetricLabels: [],
  setLineCrossingsMetricLabels: (lineCrossingsMetricLabels: MetricLabel[]) =>
    set({ lineCrossingsMetricLabels }),

  getLineCrossingsData: async (
    navigate: NavigateFunction,
    cameraId: number
  ) => {
    const AxiosUI = createApi(``);
    const {
      setLineCrossingsData,
      setLineCrossingsMetricLabels,
      getLineCrossingsData,
    } = get();

    const retryFunction = () => {
      getLineCrossingsData(navigate, cameraId);
    };

    try {
      // Fetch line crossings and metrics concurrently; use allSettled so metrics
      // failure doesn't prevent showing line crossings (e.g. /metric/ can fail or
      // return a different shape while /line_crossing/ still returns data).
      // Default (camera-wide) lines only: `preset__isnull=true` keeps
      // preset-scoped lines out of the camera-wide preview (ENG-1610).
      const [lineCrossingsSettled, metricsSettled] = await Promise.allSettled([
        AxiosUI.get(buildLineCrossingsListPath(cameraId)),
        AxiosUI.get(`/metric/?camera_id=${cameraId}`),
      ]);

      if (lineCrossingsSettled.status === "rejected") {
        universalAPIErrorHandler(
          lineCrossingsSettled.reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            console.warn("Failed to load line crossings data");
          }
        );
        return;
      }

      const lineCrossingsResponse = lineCrossingsSettled.value;

      // Set metric labels only if metrics request succeeded and has expected shape
      const allowedLabels = [
        "intruder_line_crossing",
        "vehicle_line_crossing",
        "bike_line_crossing",
      ];
      let filteredMetrics: MetricLabel[] = [];
      if (metricsSettled.status === "fulfilled") {
        const results = metricsSettled.value?.data?.results;
        if (Array.isArray(results)) {
          filteredMetrics = results
            .filter((metric: any) => allowedLabels.includes(metric.label_name))
            .map((metric: any) => ({
              id: metric.label,
              name: metric.label_name,
              display_name: metric.label_name,
              order: 0,
            }));
        }
      }
      setLineCrossingsMetricLabels(filteredMetrics);

      // Process line crossings - assign colors to each line
      const processedLines = mapLineCrossingRowsToOverlayLines(
        lineCrossingsResponse.data
      );

      // Merge this camera's data into existing state so other cameras' data is preserved
      const current = get().lineCrossingsData;
      setLineCrossingsData({ ...current, [cameraId]: processedLines });
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          console.warn("Failed to load line crossings data");
        }
      );
    }
  },
  presetLineCrossingsData: {},
  getPresetLineCrossingsData: async (
    navigate: NavigateFunction,
    cameraId: number,
    presetId: number
  ) => {
    if (get().presetLineCrossingsData[cameraId]?.[presetId]) {
      return; // cached per camera+preset; the row re-renders often
    }
    const inFlightKey = `${cameraId}:${presetId}`;
    if (presetLineCrossingsInFlight.has(inFlightKey)) {
      return;
    }
    presetLineCrossingsInFlight.add(inFlightKey);
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getPresetLineCrossingsData(navigate, cameraId, presetId);
    };
    try {
      const response = await AxiosUI.get(
        buildLineCrossingsListPath(cameraId, presetId)
      );
      const processedLines = mapLineCrossingRowsToOverlayLines(response.data);
      set((state) => ({
        presetLineCrossingsData: {
          ...state.presetLineCrossingsData,
          [cameraId]: {
            ...(state.presetLineCrossingsData[cameraId] || {}),
            [presetId]: processedLines,
          },
        },
      }));
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          console.warn("Failed to load preset line crossings data");
        }
      );
    } finally {
      presetLineCrossingsInFlight.delete(inFlightKey);
    }
  },
  checkForEvalinkSiteId: async (siteId: string, navigate: NavigateFunction) => {
    const AxiosUI = createApi(``);
    const {
      setEvalinkSiteId,
      checkForEvalinkSiteId,
      setEvalinkSite,
      getEvalinkData,
    } = get();
    const retryFunction = () => {
      checkForEvalinkSiteId(siteId, navigate);
    };
    setEvalinkSite(false);
    try {
      const response = await AxiosUI.get(`customer/${siteId}/`);
      const customer = response.data;
      if (customer.evalinksite) {
        setEvalinkSite(true);
        setEvalinkSiteId(customer.evalinksite);
        getEvalinkData(navigate);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },
  evalinkSite: false,
  setEvalinkSite: (evalinkSite: boolean) => set({ evalinkSite }),
  evalinkData: {} as EvalinkSiteData,
  setEvalinkData: (evalinkData: EvalinkSiteData) => set({ evalinkData }),
  getEvalinkData: async (navigate: NavigateFunction) => {
    const AxiosUI = createApi(``);
    const {
      setEvalinkData,
      getEvalinkData,
      setEvalinkPatchObject,
      evalinkSiteId,
    } = get();
    const retryFunction = () => {
      getEvalinkData(navigate);
    };

    try {
      const response = await AxiosUI.get(`evalink-site/${evalinkSiteId}/`);
      setEvalinkData(response.data);
      setEvalinkPatchObject({
        evalink_token: response.data.evalink_token,
        evalink_device_id: response.data.evalink_device_id,
        evalink_company_id: response.data.evalink_company_id,
      });
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },
  evalinkChangesMade: false,
  setEvalinkChangesMade: (evalinkChangesMade: boolean) =>
    set({ evalinkChangesMade }),
  evalinkPatchObject: {
    evalink_token: "",
    evalink_device_id: "",
    evalink_company_id: "",
  },
  setEvalinkPatchObject: (evalinkPatchObject: EvalinkSitePatchObject) =>
    set({ evalinkPatchObject }),
  evalinkSiteId: 0,
  setEvalinkSiteId: (evalinkSiteId: number) => set({ evalinkSiteId }),
  handlePatchEvalink: async (
    navigate: NavigateFunction,
    setBackdropLoadingScreenOn: Dispatcher<boolean>,
    setEditMode: Dispatcher<boolean>,
    onSuccess?: () => void
  ) => {
    const AxiosUI = createApi(``);
    const {
      evalinkPatchObject,
      setEvalinkPatchObject,
      setEvalinkChangesMade,
      evalinkSiteId,
      handlePatchEvalink,
      setEvalinkData,
      evalinkData,
    } = get();
    const retryFunction = () => {
      handlePatchEvalink(
        navigate,
        setBackdropLoadingScreenOn,
        setEditMode,
        onSuccess
      );
    };
    try {
      const response = await AxiosUI.patch(
        `evalink-site/${evalinkSiteId}/`,
        evalinkPatchObject
      );
      // PATCH returns the single updated resource in response.data (no .results wrapper)
      const updated = response.data;
      setEvalinkPatchObject({
        evalink_token: updated.evalink_token,
        evalink_device_id: updated.evalink_device_id,
        evalink_company_id: updated.evalink_company_id,
      });
      setEvalinkData(updated);
      setEvalinkChangesMade(false);
      setBackdropLoadingScreenOn(false);
      setEditMode(false);
      onSuccess?.();
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setEvalinkPatchObject({
            evalink_token: evalinkData.evalink_token,
            evalink_device_id: evalinkData.evalink_device_id,
            evalink_company_id: evalinkData.evalink_company_id,
          });
          alert("Problem updating evalink data. Please try again.");
          setBackdropLoadingScreenOn(false);
        }
      );
    }
  },
}));
